import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import './ApplicantPortal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const POOL_ID   = process.env.REACT_APP_COGNITO_USER_POOL_ID || '';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID    || '';

// Sessions older than this are force-expired on restore
const SESSION_MAX_MS = 8 * 60 * 60 * 1000; // 8 hours
const LOGIN_TS_KEY   = 'iu_login_at';

function getUserPool() {
  if (!POOL_ID || !CLIENT_ID) return null;
  return new CognitoUserPool({ UserPoolId: POOL_ID, ClientId: CLIENT_ID });
}

const ApplicationsCache = {
  storageKey(email) {
    return `iu_apps_${email}`;
  },
  legacyKey(email) {
    return `iu_app_${email}`;
  },
  get(email) {
    if (!email) return [];

    try {
      const current = JSON.parse(localStorage.getItem(this.storageKey(email)) || 'null');
      if (Array.isArray(current)) return current;

      const legacy = JSON.parse(localStorage.getItem(this.legacyKey(email)) || 'null');
      return legacy ? [legacy] : [];
    } catch {
      return [];
    }
  },
  set(email, applications) {
    if (!email) return;

    localStorage.setItem(this.storageKey(email), JSON.stringify(applications));

    if (applications.length) {
      localStorage.setItem(this.legacyKey(email), JSON.stringify(applications[0]));
    } else {
      localStorage.removeItem(this.legacyKey(email));
    }
  },
  clear(email) {
    if (!email) return;
    localStorage.removeItem(this.storageKey(email));
    localStorage.removeItem(this.legacyKey(email));
  },
};

/* ─── 20 conditions matching the PDF form ───────────────────────── */
const CONDITIONS = [
  'Musculoskeletal System Diseases (Joints, Arthritis, Rheumatism…)',
  'Blood Diseases (Anemia, Thalassemia…)',
  'Any Prosthesis or foreign body requiring removal?',
  'Diseases of the Digestive System (Gastric, Ulcer, Liver diseases, Gallbladder…)',
  'Endocrine Glands and Diabetes diseases (Thyroid, Gout, Triglycerides…)',
  'Diseases of the Nervous System (Epilepsy, Migraine, Stroke, Depression…)',
  'Respiratory Diseases (Asthma, Bronchitis, Tuberculosis…)',
  'Other diseases, accidents, previous or future operations?',
  'Allergy against a drug, food or other? Allergic and Skin diseases?',
  'Cardio-Vascular Diseases (Hypertension, Myocardial Infarction…)',
  'Malignant Tumors (Leukemia, Cancer…)',
  'Congenital malformation or disablement?',
  'Sexually transmitted diseases and AIDS?',
  'ENT or Eye diseases?',
  'Kidney and Urinary Tract diseases?',
  'Abuse of drugs, alcohol, or over-utilization of medicine?',
  'For women: Are you pregnant? Expected date of delivery?',
  'Anyone under medical treatment or been treated for other reasons?',
  'Are any of your parents, brothers or sisters deceased?',
  'Has any family member suffered from Diabetes, Cardio-Vascular diseases, Cancer, Hypertension, Genetic diseases, or Tuberculosis?',
];

const PLAN_OPTIONS = [
  { id: 'first_class',    label: 'First Class'    },
  { id: 'second_class',   label: 'Second Class'   },
  { id: 'third_class',    label: 'Third Class'    },
  { id: 'ambulatory_85',  label: 'Ambulatory 85%' },
  { id: 'drugs',          label: 'Drugs'          },
  { id: 'doctors_visits', label: "Dr's Visits"    },
];

const MARITAL_OPTIONS = ['Single', 'Married', 'Divorced', 'Widow'];
const RELATIONSHIPS   = ['Spouse', 'Child', 'Parent', 'Other'];

const STEPS = [
  { id: 1, label: 'Plan & Info'  },
  { id: 2, label: 'Members'     },
  { id: 3, label: 'Medical Hx.' },
  { id: 4, label: 'Documents'   },
  { id: 5, label: 'Declaration' },
];

const STEPS_CAR = [
  { id: 1, label: 'Vehicle'     },
  { id: 2, label: 'Driver'      },
  { id: 3, label: 'Declaration' },
];

const STEPS_PROPERTY = [
  { id: 1, label: 'Property'    },
  { id: 2, label: 'Coverage'    },
  { id: 3, label: 'Declaration' },
];

const STEPS_LIFE = [
  { id: 1, label: 'Coverage'    },
  { id: 2, label: 'Health'      },
  { id: 3, label: 'Declaration' },
];

const AUTH_FEATURES = [
  {
    icon: 'shield',
    title: 'Secure & Compliant',
    detail: 'Bank-level security with end-to-end encryption.',
  },
  {
    icon: 'spark',
    title: 'AI-Assisted',
    detail: 'Smarter triage and document intelligence for every submission.',
  },
  {
    icon: 'bolt',
    title: 'Faster Decisions',
    detail: 'Accelerate intake to underwriting handoff with clearer data.',
  },
];

function AuthIcon({ kind }) {
  switch (kind) {
    case 'shield':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3l7 3v5c0 4.7-2.4 7.8-7 10-4.6-2.2-7-5.3-7-10V6l7-3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M12 8v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="12" cy="16" r="1.2" fill="currentColor" />
        </svg>
      );
    case 'spark':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M9.5 5.5c1.5-1.1 3.6-1.3 5.3-.4 2.8 1.3 4 4.7 2.7 7.5-.3.7-.8 1.4-1.4 1.9l-.4.4c-.8.7-1.3 1.7-1.4 2.8h-4.6c-.1-1.1-.6-2.1-1.4-2.8l-.4-.4c-2.2-1.8-2.8-4.9-1.3-7.4.6-.9 1.4-1.8 2.4-2.3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M9.6 20h4.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M12 8.1v4.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M9.9 10.2h4.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'bolt':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M13.4 2.8L6.8 13.1h4.3L9.9 21.2l7.3-10.3h-4.3l.5-8.1z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
      );
    case 'eye':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M2.8 12s3.1-5.2 9.2-5.2S21.2 12 21.2 12s-3.1 5.2-9.2 5.2S2.8 12 2.8 12z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <circle cx="12" cy="12" r="2.8" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    case 'eye-off':
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M6.5 6.8C4 8.6 2.8 12 2.8 12s3.1 5.2 9.2 5.2c2 0 3.7-.5 5.1-1.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10.4 6.9c.5-.1 1-.1 1.6-.1 6.1 0 9.2 5.2 9.2 5.2s-.7 1.2-2 2.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14.1 14.2a3 3 0 01-4.2-4.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
}

const emptyMember = () => ({
  last_name: '', first_name: '', middle_name: '',
  relationship: 'Spouse', gender: 'M',
  dob_day: '', dob_month: '', dob_year: '',
  height: '', weight: '', nssf: 'No', occupation: '',
});

const emptyCondition = () => ({
  answer: null, member_name: '', disease: '', treatment: '', date: '', physician_hospital: '',
});

const initMedHistory = () => CONDITIONS.map(emptyCondition);

const todayStr = () => new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

function toPlanArray(value) {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
}

function formatPlanDisplay(plan) {
  const selectedPlans = toPlanArray(plan);

  if (!selectedPlans.length) {
    return 'Not specified';
  }

  return selectedPlans
    .map(id => PLAN_OPTIONS.find(option => option.id === id)?.label || id)
    .join(', ');
}

function formatStatusLabel(status) {
  if (!status) return 'Under review';
  return status.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function normalizeApplicationRecord(record) {
  if (!record || !record.applicant_id) return null;

  return {
    applicant_id:   record.applicant_id,
    submitted_at:   record.submitted_at || record.created_at || record.created_date || new Date().toISOString(),
    applicant_name: record.applicant_name || record.name || '',
    plan:           toPlanArray(record.plan || record.plans),
    status:         record.status || 'under_review',
    pdf_parsed:     Boolean(record.pdf_parsed),
    email:          record.email || '',
    members:        Array.isArray(record.members) ? record.members : [],
    members_count:  record.members_count ?? (Array.isArray(record.members) ? record.members.length : 0),
  };
}

function getApplicationTimestamp(record) {
  const parsed = Date.parse(record?.submitted_at || '');
  return Number.isNaN(parsed) ? 0 : parsed;
}

function mergeApplications(existing, incoming) {
  const merged = new Map();

  const upsert = (record) => {
    const normalized = normalizeApplicationRecord(record);
    if (!normalized) return;

    const current = merged.get(normalized.applicant_id);
    merged.set(normalized.applicant_id, current ? {
      ...current,
      ...normalized,
      applicant_name: normalized.applicant_name || current.applicant_name,
      plan: normalized.plan.length ? normalized.plan : current.plan,
      submitted_at: normalized.submitted_at || current.submitted_at,
      status: normalized.status || current.status,
      pdf_parsed: normalized.pdf_parsed || current.pdf_parsed,
      email: normalized.email || current.email,
    } : normalized);
  };

  existing.forEach(upsert);
  incoming.forEach(upsert);

  return Array.from(merged.values()).sort((left, right) => {
    const timeDiff = getApplicationTimestamp(right) - getApplicationTimestamp(left);
    if (timeDiff !== 0) return timeDiff;
    return (right.applicant_id || '').localeCompare(left.applicant_id || '');
  });
}

async function fetchApplicationsForEmail(email) {
  const resp = await fetch(`${API_URL}/application-status?email=${encodeURIComponent(email.toLowerCase())}`);
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }

  const data = await resp.json();
  if (Array.isArray(data.applications)) {
    return data.applications.map(normalizeApplicationRecord).filter(Boolean);
  }

  if (data.found) {
    const latestRecord = normalizeApplicationRecord(data);
    return latestRecord ? [latestRecord] : [];
  }

  return [];
}

/* ════════════════════════════════════════════════════════════════
   AUTH SCREEN
   ════════════════════════════════════════════════════════════════ */
function AuthScreen({ onLogin }) {
  const [mode, setMode] = useState('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingPass, setPendingPass] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const err = (msg) => {
    setError(msg);
    setBusy(false);
  };

  const doSignUp = () => {
    const pool = getUserPool();
    if (!pool) return err('Auth service is not configured yet.');
    if (!name.trim()) return err('Full name is required.');
    if (!email.includes('@')) return err('Please enter a valid email address.');
    if (password.length < 8) return err('Password must be at least 8 characters.');
    if (password !== confirm) return err('Passwords do not match.');

    const attrs = [new CognitoUserAttribute({ Name: 'name', Value: name.trim() })];
    if (phone.trim()) {
      attrs.push(new CognitoUserAttribute({ Name: 'phone_number', Value: phone.trim() }));
    }

    pool.signUp(email.toLowerCase(), password, attrs, null, (e) => {
      if (e) return err(e.message || 'Sign-up failed.');
      setPendingEmail(email.toLowerCase());
      setPendingPass(password);
      setInfo(`A verification code was sent to ${email}.`);
      setCode('');
      setMode('verify');
      setBusy(false);
    });
  };

  const doVerify = () => {
    const pool = getUserPool();
    if (!pool) return err('Auth service is not configured yet.');
    if (!code.trim()) return err('Please enter the 6-digit code from your email.');

    const cognitoUser = new CognitoUser({ Username: pendingEmail, Pool: pool });
    cognitoUser.confirmRegistration(code.trim(), true, (e) => {
      if (e) return err(e.message || 'Verification failed.');

      const authDetails = new AuthenticationDetails({
        Username: pendingEmail,
        Password: pendingPass,
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          setBusy(false);
          localStorage.setItem(LOGIN_TS_KEY, Date.now().toString());
          const pl = session.getIdToken().payload;
          const groups = pl['cognito:groups'] || [];
          onLogin({ name: pl.name || pl.email, email: pendingEmail, phone: pl.phone_number || '', groups });
        },
        onFailure: () => {
          setBusy(false);
          setInfo('Account verified! Please sign in.');
          setMode('signin');
          setEmail(pendingEmail);
        },
      });
    });
  };

  const doSignIn = () => {
    const pool = getUserPool();
    if (!pool) return err('Auth service is not configured yet.');
    if (!email || !password) return err('Email and password are required.');

    const cognitoUser = new CognitoUser({ Username: email.toLowerCase(), Pool: pool });
    const authDetails = new AuthenticationDetails({
      Username: email.toLowerCase(),
      Password: password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        setBusy(false);
        localStorage.setItem(LOGIN_TS_KEY, Date.now().toString());
        const pl = session.getIdToken().payload;
        const groups = pl['cognito:groups'] || [];
        onLogin({ name: pl.name || pl.email, email: email.toLowerCase(), phone: pl.phone_number || '', groups });
      },
      onFailure: (e) => {
        if (e.code === 'UserNotConfirmedException') {
          setPendingEmail(email.toLowerCase());
          setPendingPass(password);
          setInfo('Your email is not yet verified. Enter the code we sent you.');
          setCode('');
          setMode('verify');
          setBusy(false);
        } else {
          err(e.message || 'Sign-in failed. Please check your credentials.');
        }
      },
    });
  };

  const resendCode = () => {
    const pool = getUserPool();
    if (!pool || !pendingEmail) return;

    new CognitoUser({ Username: pendingEmail, Pool: pool }).resendConfirmationCode((e) => {
      if (e) setError(e.message);
      else setInfo('A new code has been sent to your email.');
    });
  };

  const submit = (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setBusy(true);

    if (mode === 'signup') doSignUp();
    else if (mode === 'verify') doVerify();
    else doSignIn();
  };

  const logoMark = (
    <div className="auth-logo">
      <div className="auth-logo-icon">
        <svg viewBox="0 0 32 32" fill="none" width="26" height="26" aria-hidden="true">
          <path d="M6 22h4l2-8 2 8 2-8 2 8 2-8 2 8h4" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div>
        <div className="auth-logo-name">Nebulane Risk Desk</div>
        <div className="auth-logo-sub">Nebulane Applicant Workspace</div>
      </div>
    </div>
  );

  const authHeader = (
    <div className="auth-shell-header">
      {logoMark}
      <div className="auth-shell-context">
        <span className="auth-shell-badge">Applicant</span>
        <span className="auth-shell-context-copy">Secure digital intake</span>
      </div>
    </div>
  );

  const authExperience = (
    <section className="auth-hero-panel">
      <div className="auth-hero-content">
        <div className="auth-hero-copy-block">
          <div className="auth-kicker">Underwrite smarter. Decide faster.</div>
          <h1 className="auth-hero-title">
            AI-powered intake built for the future of insurance
            <span className="auth-hero-title-accent">.</span>
          </h1>
        </div>
        <p className="auth-hero-copy">
          Streamlined onboarding, intelligent data collection, and secure underwriting support all in one seamless Nebulane workspace.
        </p>
        <div className="auth-feature-list">
          {AUTH_FEATURES.map((item) => (
            <div key={item.title} className="auth-feature">
              <div className={`auth-feature-icon auth-feature-icon-${item.icon}`}>
                <AuthIcon kind={item.icon} />
              </div>
              <div className="auth-feature-copy">
                <strong>{item.title}</strong>
                <span>{item.detail}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderCardShell = (children, verifyCard = false) => (
    <div className="auth-screen">
      <div className="auth-shell">
        <div className="auth-shell-backdrop" aria-hidden="true" />
        {authHeader}
        {authExperience}
        <div className="auth-panel">
          <div className={`auth-card${verifyCard ? ' auth-card-verify' : ''}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  if (mode === 'verify') {
    return renderCardShell(
      <>
        <div className="auth-card-kicker">Account verification</div>
        <h2 className="auth-title">Verify your email</h2>
        <p className="auth-subtitle">
          We sent a 6-digit code to <strong>{pendingEmail}</strong>. Check your inbox and spam folder.
        </p>
        {info && <div className="auth-info">{info}</div>}
        {error && <div className="auth-error">{error}</div>}
        <form className="auth-fields" onSubmit={submit}>
          <div className="auth-field">
            <label className="auth-label">Verification Code</label>
            <input
              className="auth-input"
              type="text"
              inputMode="numeric"
              placeholder="123456"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
            />
          </div>
          <button className="auth-btn" type="submit" disabled={busy}>
            {busy ? 'Verifying...' : 'Verify & Sign In'}
          </button>
        </form>
        <div className="auth-switch">
          Didn't receive a code?{' '}
          <button className="auth-switch-link" type="button" onClick={resendCode}>Resend</button>
        </div>
      </>,
      true,
    );
  }

  return renderCardShell(
    <>
      <div className="auth-card-kicker">{mode === 'signup' ? 'Create your account' : 'Secure sign in'}</div>
      <h2 className="auth-title">{mode === 'signup' ? 'Start your application' : 'Welcome back'}</h2>
      <p className="auth-subtitle">
        {mode === 'signup'
          ? 'Set up your account to begin a guided insurance application.'
          : 'Sign in to continue an application or review your submitted files.'}
      </p>
      {info && <div className="auth-info">{info}</div>}
      <form className="auth-fields" onSubmit={submit}>
        {mode === 'signup' && (
          <div className="auth-field">
            <label className="auth-label">Full Name</label>
            <input
              className="auth-input"
              type="text"
              placeholder="Jane Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        )}
        <div className="auth-field">
          <label className="auth-label">Email Address</label>
          <input
            className="auth-input"
            type="email"
            placeholder="jane@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </div>
        {mode === 'signup' && (
          <div className="auth-field">
            <label className="auth-label">Phone Number (optional)</label>
            <input
              className="auth-input"
              type="tel"
              placeholder="+961 71 000 000"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        )}
        <div className="auth-field">
          <label className="auth-label">Password</label>
          <div className="auth-input-shell">
            <input
              className="auth-input auth-input-password"
              type={showPassword ? 'text' : 'password'}
              placeholder={mode === 'signup' ? 'At least 8 characters' : 'Enter your password'}
              value={password}
              onChange={(e) => setPass(e.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
            <button
              className="auth-password-toggle"
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((current) => !current)}
            >
              <AuthIcon kind={showPassword ? 'eye-off' : 'eye'} />
            </button>
          </div>
        </div>
        {mode === 'signup' && (
          <div className="auth-field">
            <label className="auth-label">Confirm Password</label>
            <div className="auth-input-shell">
              <input
                className="auth-input auth-input-password"
                type={showConfirm ? 'text' : 'password'}
                placeholder="Confirm your password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
              <button
                className="auth-password-toggle"
                type="button"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                onClick={() => setShowConfirm((current) => !current)}
              >
                <AuthIcon kind={showConfirm ? 'eye-off' : 'eye'} />
              </button>
            </div>
          </div>
        )}
        {error && <div className="auth-error">{error}</div>}
        <button className="auth-btn" type="submit" disabled={busy}>
          {busy ? 'Please wait...' : mode === 'signup' ? 'Create Account' : 'Sign In'}
        </button>
      </form>
      <div className="auth-divider">or</div>
      <div className="auth-switch">
        {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
        <button
          className="auth-switch-link"
          type="button"
          onClick={() => {
            setMode((current) => (current === 'signin' ? 'signup' : 'signin'));
            setError('');
            setInfo('');
            setShowPassword(false);
            setShowConfirm(false);
          }}
        >
          {mode === 'signin' ? 'Sign up' : 'Sign in'}
        </button>
      </div>
    </>,
  );
}

/* ════════════════════════════════════════════════════════════════
   SHARED FIELD BLOCK  (module-level — keeps stable identity)
   ════════════════════════════════════════════════════════════════ */
function FieldBlock({ label, req, children, full }) {
  return (
    <div className={`field-block${full ? ' fg-full' : ''}`}>
      <label className="field-label">{label}{req && <span className="field-required">*</span>}</label>
      {children}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   STEP 1 — Plan & Principal Info
   ════════════════════════════════════════════════════════════════ */
function StepPlanInfo({ selectedPlans, setSelectedPlans, p, upP }) {
  const toggle = (id) =>
    setSelectedPlans(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <>
      <div className="form-section-head">
        <div className="form-section-tag">Section A</div>
        <h2 className="form-section-title">Plan Selection &amp; Proposer Details</h2>
        <p className="form-section-desc">Select your desired coverage options and provide your personal information.</p>
      </div>

      <div className="field-block" style={{ marginBottom: 24 }}>
        <label className="field-label">Medical Plan Coverage<span className="field-required">*</span></label>
        <div className="plan-options">
          {PLAN_OPTIONS.map(opt => (
            <label key={opt.id} className={`plan-option${selectedPlans.includes(opt.id) ? ' selected' : ''}`}>
              <input type="checkbox" checked={selectedPlans.includes(opt.id)} onChange={() => toggle(opt.id)} />
              <span className="plan-option-label">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="fg-2">
        <FieldBlock label="Last Name" req>
          <input className="field-input" placeholder="Smith" value={p.last_name} onChange={e => upP('last_name', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="First Name" req>
          <input className="field-input" placeholder="Jane" value={p.first_name} onChange={e => upP('first_name', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="Middle Name">
          <input className="field-input" placeholder="Marie" value={p.middle_name} onChange={e => upP('middle_name', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="Gender" req>
          <div className="seg-row">
            {[['M', 'Male'], ['F', 'Female']].map(([v, l]) => (
              <button key={v} type="button" className={`seg-btn${p.gender === v ? ' on' : ''}`} onClick={() => upP('gender', v)}>{l}</button>
            ))}
          </div>
        </FieldBlock>

        <FieldBlock label="Date of Birth - Day" req>
          <input className="field-input" type="number" placeholder="DD" min="1" max="31" value={p.dob_day} onChange={e => upP('dob_day', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="Month">
          <input className="field-input" type="number" placeholder="MM" min="1" max="12" value={p.dob_month} onChange={e => upP('dob_month', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="Year">
          <input className="field-input" type="number" placeholder="YYYY" min="1920" max="2006" value={p.dob_year} onChange={e => upP('dob_year', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="Place of Birth">
          <input className="field-input" placeholder="Beirut" value={p.place_of_birth} onChange={e => upP('place_of_birth', e.target.value)} />
        </FieldBlock>

        <FieldBlock label="Marital Status" req full>
          <div className="seg-row">
            {MARITAL_OPTIONS.map(v => (
              <button key={v} type="button" className={`seg-btn${p.marital_status === v ? ' on' : ''}`} onClick={() => upP('marital_status', v)}>{v}</button>
            ))}
          </div>
        </FieldBlock>

        <FieldBlock label="Nationality" req>
          <input className="field-input" placeholder="Lebanese" value={p.nationality} onChange={e => upP('nationality', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="ID Number" req>
          <input className="field-input" placeholder="National ID / Passport No." value={p.national_id} onChange={e => upP('national_id', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="Financial Number">
          <input className="field-input" placeholder="Financial / Tax number" value={p.financial_number} onChange={e => upP('financial_number', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="Occupation / Profession" req>
          <input className="field-input" placeholder="Engineer" value={p.occupation} onChange={e => upP('occupation', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="Annual Income (USD)" req>
          <input className="field-input" type="number" placeholder="e.g. 75000" min="0" value={p.annual_income} onChange={e => upP('annual_income', e.target.value)} />
        </FieldBlock>

        <FieldBlock label="Tel">
          <input className="field-input" type="tel" placeholder="+961 1 000 000" value={p.phone} onChange={e => upP('phone', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="Mobile">
          <input className="field-input" type="tel" placeholder="+961 71 000 000" value={p.mobile} onChange={e => upP('mobile', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="Email Address">
          <input className="field-input" type="email" value={p.email} readOnly />
        </FieldBlock>

        <FieldBlock label="Building / Apartment">
          <input className="field-input" placeholder="Bldg. Rizk, Apt 3" value={p.building} onChange={e => upP('building', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="Street">
          <input className="field-input" placeholder="Main Street" value={p.street} onChange={e => upP('street', e.target.value)} />
        </FieldBlock>
        <FieldBlock label="City" req>
          <input className="field-input" placeholder="Beirut" value={p.city} onChange={e => upP('city', e.target.value)} />
        </FieldBlock>
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   STEP 2 — Members
   ════════════════════════════════════════════════════════════════ */
function StepMembers({ members, addMember, removeMember, updateMember }) {
  return (
    <>
      <div className="form-section-head">
        <div className="form-section-tag">Section B</div>
        <h2 className="form-section-title">Members to be Insured</h2>
        <p className="form-section-desc">Add your spouse, children, or other dependents you wish to include in this policy.</p>
      </div>

      <div className="members-header">
        <span className="members-title">Additional Members ({members.length})</span>
        {members.length < 5 && (
          <button type="button" className="add-member-btn" onClick={addMember}>
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Member
          </button>
        )}
      </div>

      {members.length === 0 && (
        <div className="empty-members">No additional members added. Click "Add Member" to include dependents.</div>
      )}

      {members.map((m, i) => (
        <div key={i} className="member-card">
          <div className="member-card-head">
            <span className="member-badge">Member {i + 2}</span>
            <button type="button" className="member-remove" onClick={() => removeMember(i)}>×</button>
          </div>
          <div className="fg-3">
            <div className="field-block">
              <label className="field-label">Last Name</label>
              <input className="field-input" placeholder="Smith" value={m.last_name} onChange={e => updateMember(i, 'last_name', e.target.value)} />
            </div>
            <div className="field-block">
              <label className="field-label">First Name</label>
              <input className="field-input" placeholder="John" value={m.first_name} onChange={e => updateMember(i, 'first_name', e.target.value)} />
            </div>
            <div className="field-block">
              <label className="field-label">Middle Name</label>
              <input className="field-input" placeholder="-" value={m.middle_name} onChange={e => updateMember(i, 'middle_name', e.target.value)} />
            </div>
            <div className="field-block">
              <label className="field-label">Relationship</label>
              <select className="field-input" value={m.relationship} onChange={e => updateMember(i, 'relationship', e.target.value)}>
                {RELATIONSHIPS.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="field-block">
              <label className="field-label">Gender</label>
              <div className="seg-row">
                {[['M', 'Male'], ['F', 'Female']].map(([v, l]) => (
                  <button key={v} type="button" className={`seg-btn${m.gender === v ? ' on' : ''}`} onClick={() => updateMember(i, 'gender', v)}>{l}</button>
                ))}
              </div>
            </div>
            <div className="field-block">
              <label className="field-label">Date of Birth</label>
              <input className="field-input" type="date"
                value={m.dob_year && m.dob_month && m.dob_day ? `${m.dob_year.padStart(4,'0')}-${m.dob_month.padStart(2,'0')}-${m.dob_day.padStart(2,'0')}` : ''}
                onChange={e => {
                  const val = e.target.value;
                  if (val) {
                    const [y, mo, d] = val.split('-');
                    updateMember(i, 'dob_day', String(parseInt(d, 10)));
                    updateMember(i, 'dob_month', String(parseInt(mo, 10)));
                    updateMember(i, 'dob_year', y);
                  } else {
                    updateMember(i, 'dob_day', '');
                    updateMember(i, 'dob_month', '');
                    updateMember(i, 'dob_year', '');
                  }
                }} />
            </div>
            <div className="field-block">
              <label className="field-label">Height (cm)</label>
              <input className="field-input" type="number" placeholder="170" value={m.height} onChange={e => updateMember(i, 'height', e.target.value)} />
            </div>
            <div className="field-block">
              <label className="field-label">Weight (kg)</label>
              <input className="field-input" type="number" placeholder="70" value={m.weight} onChange={e => updateMember(i, 'weight', e.target.value)} />
            </div>
            <div className="field-block">
              <label className="field-label">NSSF</label>
              <div className="seg-row">
                {['Yes', 'No'].map(v => (
                  <button key={v} type="button" className={`seg-btn${m.nssf === v ? ' on' : ''}`} onClick={() => updateMember(i, 'nssf', v)}>{v}</button>
                ))}
              </div>
            </div>
            <div className="field-block fg-full">
              <label className="field-label">Occupation</label>
              <input className="field-input" placeholder="Teacher" value={m.occupation} onChange={e => updateMember(i, 'occupation', e.target.value)} />
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   STEP 3 — Medical History
   ════════════════════════════════════════════════════════════════ */
function StepMedHistory({ medHistory, setCondition }) {
  const flagged = medHistory.filter(c => c.answer === true).length;
  return (
    <>
      <div className="form-section-head">
        <div className="form-section-tag">Section C</div>
        <h2 className="form-section-title">Medical History Declaration</h2>
        <p className="form-section-desc">Have you or any member listed above ever been diagnosed with, or suffered from any of the following?</p>
      </div>

      <div className="med-history-intro">
        Answer Yes or No for each condition. If Yes, provide details in the expanded panel.
        Accurate information ensures correct coverage and avoids claim complications.
        {flagged > 0 && <strong> - {flagged} condition{flagged > 1 ? 's' : ''} flagged</strong>}
      </div>

      {CONDITIONS.map((cond, i) => {
        const c = medHistory[i];
        return (
          <div key={i} className={`med-condition${c.answer === true ? ' flagged' : ''}`}>
            <div className="med-condition-row">
              <div className="med-condition-num">{i + 1}</div>
              <div className="med-condition-text">{cond}</div>
              <div className="med-yn">
                <button type="button" className={`yn-btn${c.answer === true ? ' yes-on' : ''}`}
                  onClick={() => setCondition(i, 'answer', c.answer === true ? null : true)}>YES</button>
                <button type="button" className={`yn-btn${c.answer === false ? ' no-on' : ''}`}
                  onClick={() => setCondition(i, 'answer', c.answer === false ? null : false)}>NO</button>
              </div>
            </div>
            {c.answer === true && (
              <div className="med-condition-details">
                <div className="med-detail-row">
                  <div className="field-block">
                    <label className="field-label">Member Name</label>
                    <input className="field-input" placeholder="Name of the affected member"
                      value={c.member_name} onChange={e => setCondition(i, 'member_name', e.target.value)} />
                  </div>
                  <div className="field-block">
                    <label className="field-label">Disease / Condition</label>
                    <input className="field-input" placeholder="e.g. Type 2 Diabetes"
                      value={c.disease} onChange={e => setCondition(i, 'disease', e.target.value)} />
                  </div>
                </div>
                <div className="med-detail-row">
                  <div className="field-block">
                    <label className="field-label">Treatment</label>
                    <input className="field-input" placeholder="Treatment / Medication"
                      value={c.treatment} onChange={e => setCondition(i, 'treatment', e.target.value)} />
                  </div>
                  <div className="field-block">
                    <label className="field-label">Date</label>
                    <input className="field-input" placeholder="MM/YYYY"
                      value={c.date} onChange={e => setCondition(i, 'date', e.target.value)} />
                  </div>
                </div>
                <div className="field-block">
                  <label className="field-label">Physician / Hospital</label>
                  <input className="field-input" placeholder="Dr. Name / Hospital Name"
                    value={c.physician_hospital} onChange={e => setCondition(i, 'physician_hospital', e.target.value)} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

/* ────────────────────────────────────────────────────────────────
   Per-member document upload widget (own ref per instance)
   ──────────────────────────────────────────────────────────────── */
function MemberDocUpload({ memberIndex, memberName, file, onFileChange }) {
  const ref = useRef(null);
  return (
    <div className="member-doc-zone">
      <input ref={ref} type="file" accept="application/pdf" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files[0]; if (f) onFileChange(memberIndex, f); e.target.value = ''; }} />
      {file ? (
        <div className="member-doc-loaded">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
          </svg>
          <span className="member-doc-name">{file.name}</span>
          <span className="member-doc-size">({(file.size / 1024).toFixed(1)} KB)</span>
          <button type="button" className="member-doc-remove"
            onClick={() => { onFileChange(memberIndex, null); ref.current.value = ''; }}>Remove</button>
        </div>
      ) : (
        <button type="button" className="member-doc-upload-btn" onClick={() => ref.current?.click()}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          Upload medical report for {memberName || `Member ${memberIndex + 2}`}
        </button>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   STEP 4 — Documents
   ════════════════════════════════════════════════════════════════ */
function StepDocuments({ pdfFile, setPdfFile, fileRef, dragging, setDragging, onDrop, members, memberPdfFiles, setMemberPdfFile }) {
  return (
    <>
      <div className="form-section-head">
        <div className="form-section-tag">Section D</div>
        <h2 className="form-section-title">Medical Document Upload</h2>
        <p className="form-section-desc">Upload your most recent blood test or medical report. Our AI will extract and structure the data automatically.</p>
      </div>

      <div
        className={`dropzone${dragging ? ' drag-over' : ''}${pdfFile ? ' loaded' : ''}`}
        onDrop={onDrop}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onClick={() => !pdfFile && fileRef.current?.click()}
      >
        <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files[0]; if (f) setPdfFile(f); e.target.value = ''; }} />
        <div className="dz-icon">
          {pdfFile ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          )}
        </div>
        {pdfFile ? (
          <>
            <div className="dz-title">Document Ready</div>
            <div className="dz-file-info">
              <span className="dz-filename">{pdfFile.name}</span>
              <span className="dz-filesize">({(pdfFile.size / 1024).toFixed(1)} KB)</span>
              <button type="button" className="dz-remove"
                onClick={e => { e.stopPropagation(); setPdfFile(null); if (fileRef.current) fileRef.current.value = ''; }}>
                Remove
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="dz-title">Drop your PDF here or click to browse</div>
            <div className="dz-sub">Accepted format: PDF. Maximum file size: 10 MB.</div>
          </>
        )}
      </div>

      <div className="dz-hint dz-hint-blue" style={{ marginTop: 16 }}>
        <strong>AI-powered extraction:</strong> Claude AI will parse your medical report and extract lab values, cholesterol, blood pressure, medications, and conditions, creating a structured record for the underwriting team.
      </div>
      <div className="dz-hint dz-hint-gray" style={{ marginTop: 10 }}>
        <strong>Optional:</strong> You may continue without a document. The underwriting team may request additional medical information if needed.
      </div>

      {members.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div className="form-section-tag" style={{ marginBottom: 10 }}>Member Documents</div>
          <p style={{ fontSize: '0.83rem', color: 'var(--p-muted)', marginBottom: 14 }}>
            Upload a medical report for each additional member if available.
          </p>
          {members.map((m, i) => {
            const name = [m.first_name, m.last_name].filter(Boolean).join(' ') || `Member ${i + 2}`;
            return (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--p-label)', marginBottom: 6 }}>
                  {name} <span style={{ fontWeight: 400, color: 'var(--p-muted)' }}>({m.relationship})</span>
                </div>
                <MemberDocUpload
                  memberIndex={i}
                  memberName={name}
                  file={memberPdfFiles[i] || null}
                  onFileChange={setMemberPdfFile}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   STEP 5 — Declaration & Review
   ════════════════════════════════════════════════════════════════ */
function StepDeclaration({ p, selectedPlans, members, medHistory, pdfFile, declared, setDeclared, signDate, setSignDate, signCity, setSignCity }) {
  const flagged    = medHistory.filter(c => c.answer === true).length;
  const planLabels = selectedPlans.map(id => PLAN_OPTIONS.find(o => o.id === id)?.label || id).join(', ') || 'None selected';
  return (
    <>
      <div className="form-section-head">
        <div className="form-section-tag">Section E</div>
        <h2 className="form-section-title">Declaration &amp; Review</h2>
        <p className="form-section-desc">Review your application summary and accept the declaration before submitting.</p>
      </div>

      <div className="review-table" style={{ marginBottom: 14 }}>
        <div className="review-table-head">Application Summary</div>
        {[
          ['Plan',              planLabels],
          ['Applicant',         `${p.first_name} ${p.last_name}`.trim() || '-'],
          ['Date of Birth',     `${p.dob_day}/${p.dob_month}/${p.dob_year}`],
          ['Marital Status',    p.marital_status || '-'],
          ['Nationality',       p.nationality    || '-'],
          ['National ID',       p.national_id    || '-'],
          ['Occupation',        p.occupation     || '-'],
          ['Address',           [p.building, p.street, p.city].filter(Boolean).join(', ') || '-'],
          ['Members',           `${1 + members.length} insured`],
          ['Flagged Conditions', flagged > 0 ? `${flagged} condition${flagged > 1 ? 's' : ''} declared` : 'None'],
          ['Medical Document',  pdfFile ? pdfFile.name : 'Not uploaded'],
        ].map(([k, v]) => (
          <div key={k} className="review-row">
            <div className="review-key">{k}</div>
            <div className="review-val">{v}</div>
          </div>
        ))}
      </div>

      <div className="declaration-box">
        I, the undersigned, hereby declare that all statements made in this proposal form are true, complete, and accurate to the best of my knowledge and belief. I understand that any misrepresentation, omission, or concealment of facts may result in the rejection of the application or cancellation of the policy. I authorize the insurance company to obtain any medical information necessary to assess this application. I agree to the terms and conditions of the proposed insurance policy.
      </div>

      <div className="decl-check-row" onClick={() => setDeclared(d => !d)}>
        <input type="checkbox" className="decl-checkbox" checked={declared} onChange={() => setDeclared(d => !d)} />
        <span className="decl-check-text">
          I have read and understood the declaration above and confirm all information provided is true and complete.
        </span>
      </div>

      <div className="fg-2">
        <div className="field-block">
          <label className="field-label">Date<span className="field-required">*</span></label>
          <input className="field-input" value={signDate} onChange={e => setSignDate(e.target.value)} placeholder="DD/MM/YYYY" />
        </div>
        <div className="field-block">
          <label className="field-label">Place (City)</label>
          <input className="field-input" value={signCity} onChange={e => setSignCity(e.target.value)} placeholder="Beirut" />
        </div>
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   APPLICATION FORM  (5-step wizard)
   ════════════════════════════════════════════════════════════════ */
function ApplicationForm({ user, onSubmitted }) {
  const [step, setStep]   = useState(1);
  const [error, setError] = useState('');

  const [selectedPlans, setSelectedPlans] = useState([]);

  const [p, setP] = useState({
    last_name: '', first_name: '', middle_name: '',
    marital_status: 'Single',
    dob_day: '', dob_month: '', dob_year: '',
    place_of_birth: '', nationality: '', national_id: '',
    financial_number: '',
    gender: 'M', occupation: '', annual_income: '',
    phone: user.phone || '', mobile: '', email: user.email,
    building: '', street: '', city: '',
  });
  const upP = (k, v) => setP(prev => ({ ...prev, [k]: v }));

  const [members, setMembers]         = useState([]);
  const [memberPdfFiles, setMemberPdfFiles] = useState([]);

  const addMember = () => {
    setMembers(m => [...m, { ...emptyMember(), last_name: p.last_name }]);
    setMemberPdfFiles(f => [...f, null]);
  };
  const removeMember = (i) => {
    setMembers(m => m.filter((_, idx) => idx !== i));
    setMemberPdfFiles(f => f.filter((_, idx) => idx !== i));
  };
  const updateMember    = (i, k, v) => setMembers(m => m.map((mem, idx) => idx === i ? { ...mem, [k]: v } : mem));
  const setMemberPdfFile = (i, file) => setMemberPdfFiles(f => f.map((v, idx) => idx === i ? file : v));

  const [medHistory, setMedHistory] = useState(initMedHistory());
  const setCondition = (i, k, v) => setMedHistory(h => h.map((c, idx) => idx === i ? { ...c, [k]: v } : c));

  const [pdfFile, setPdfFile]     = useState(null);
  const [dragging, setDragging]   = useState(false);
  const [declared, setDeclared]   = useState(false);
  const [signDate, setSignDate]   = useState(todayStr());
  const [signCity, setSignCity]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const onDrop = useCallback(e => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && f.type === 'application/pdf') setPdfFile(f);
  }, []);

  const validate = () => {
    setError('');
    if (step === 1) {
      if (!p.last_name.trim() || !p.first_name.trim()) return setError('First name and last name are required.') || false;
      if (!p.dob_day || !p.dob_month || !p.dob_year)  return setError('Date of birth is required.')             || false;
      if (!p.nationality.trim())                       return setError('Nationality is required.')               || false;
      if (!p.national_id.trim())                       return setError('National ID / Passport is required.')    || false;
      if (!p.occupation.trim())                        return setError('Occupation is required.')                || false;
      if (!p.city.trim())                              return setError('City is required.')                      || false;
    }
    return true;
  };

  const next = () => { if (validate()) { setStep(s => s + 1); window.scrollTo(0, 0); } };
  const back = () => { setError(''); setStep(s => s - 1); window.scrollTo(0, 0); };

  const deriveHealthStatus = (mh) => {
    const n = mh.filter(c => c.answer === true).length;
    if (n === 0) return 'Excellent';
    if (n <= 2)  return 'Good';
    if (n <= 5)  return 'Fair';
    return 'Poor';
  };

  const submit = async () => {
    if (!declared) return setError('You must accept the declaration before submitting.');
    setSubmitting(true); setError('');
    try {
      const toBase64 = (file) => new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload  = () => res(reader.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });

      let pdfBase64 = null;
      if (pdfFile) pdfBase64 = await toBase64(pdfFile);

      const memberDocs = (await Promise.all(
        memberPdfFiles.map((f, i) =>
          f ? toBase64(f).then(b64 => ({ member_index: i, filename: f.name, pdf_base64: b64 })) : Promise.resolve(null)
        )
      )).filter(Boolean);

      const dob    = `${p.dob_year}-${String(p.dob_month).padStart(2, '0')}-${String(p.dob_day).padStart(2, '0')}`;
      const ageYrs = new Date().getFullYear() - parseInt(p.dob_year || '0', 10);

      const payload = {
        applicant: {
          name:             `${p.first_name} ${p.last_name}`,
          age:              ageYrs,
          gender:           p.gender === 'F' ? 'Female' : 'Male',
          occupation:       p.occupation,
          income:           parseInt(p.annual_income || '0', 10),
          health_status:    deriveHealthStatus(medHistory),
          smoker:           false,

          previous_claims:  0,
          marital_status:   p.marital_status,
          dependents:       members.filter(m => m.relationship === 'Child').length,
          family_history: {
            heart_disease:  medHistory[9].answer  === true,   // Cardio-Vascular (idx 9)
            diabetes:       medHistory[4].answer  === true,   // Endocrine/Diabetes (idx 4)
            cancer:         medHistory[10].answer === true,   // Malignant Tumors (idx 10)
          },
          plans:            selectedPlans,
          nationality:      p.nationality,
          national_id:      p.national_id,
          financial_number: p.financial_number,
          date_of_birth:    dob,
          place_of_birth:   p.place_of_birth,
          phone:            p.phone,
          mobile:           p.mobile,
          email:            p.email,
          address:          [p.building, p.street, p.city].filter(Boolean).join(', '),
          members,
          medical_history_flags: medHistory.map((c, i) => ({
            condition: CONDITIONS[i].substring(0, 80),
            answer:    c.answer,
            details:   c.answer ? {
              member_name:        c.member_name,
              disease:            c.disease,
              treatment:          c.treatment,
              date:               c.date,
              physician_hospital: c.physician_hospital,
            } : null,
          })),
          declaration_date: signDate,
          declaration_city: signCity,
        },
        medical_document:  pdfBase64,
        member_documents:  memberDocs,
      };

      const resp = await fetch(`${API_URL}/submit-application`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || `HTTP ${resp.status}`);

      const appRecord = {
        applicant_id:   data.applicant_id,
        submitted_at:   new Date().toISOString(),
        applicant_name: `${p.first_name} ${p.last_name}`,
        gender:         p.gender,
        dob_day:        p.dob_day,
        dob_month:      p.dob_month,
        dob_year:       p.dob_year,
        plan:           selectedPlans,
        status:         'under_review',
        pdf_parsed:     data.pdf_parsed,
        email:          p.email,
        members:        members,
        members_count:  members.length,
      };
      onSubmitted(appRecord);
    } catch (e) {
      setError(e.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = ((step - 1) / (STEPS.length - 1)) * 100;

  const renderStep = () => {
    switch (step) {
      case 1: return <StepPlanInfo selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} p={p} upP={upP} />;
      case 2: return <StepMembers  members={members} addMember={addMember} removeMember={removeMember} updateMember={updateMember} />;
      case 3: return <StepMedHistory medHistory={medHistory} setCondition={setCondition} />;
      case 4: return <StepDocuments  pdfFile={pdfFile} setPdfFile={setPdfFile} fileRef={fileRef} dragging={dragging} setDragging={setDragging} onDrop={onDrop} members={members} memberPdfFiles={memberPdfFiles} setMemberPdfFile={setMemberPdfFile} />;
      case 5: return <StepDeclaration p={p} selectedPlans={selectedPlans} members={members} medHistory={medHistory} pdfFile={pdfFile}
                       declared={declared} setDeclared={setDeclared} signDate={signDate} setSignDate={setSignDate}
                       signCity={signCity} setSignCity={setSignCity} />;
      default: return null;
    }
  };

  return (
    <div className="form-shell">
      <div className="form-hero">
        <div>
          <div className="form-kicker">Coverage application</div>
          <h1 className="form-hero-title">Build a complete underwriting-ready application.</h1>
          <p className="form-hero-copy">
            Move step by step through plan selection, family members, medical history, supporting documents, and declaration.
          </p>
        </div>
        <div className="form-hero-side">
          <span className="form-hero-step">Step {step}</span>
          <span className="form-hero-total">of {STEPS.length}</span>
        </div>
      </div>

      <div className="form-progress">
        <div className="form-progress-line">
          <div className="form-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        {STEPS.map(s => (
          <div key={s.id} className="form-step-item">
            <div className={`form-step-bubble${step === s.id ? ' active' : step > s.id ? ' done' : ''}`}>
              {step > s.id
                ? <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                : s.id}
            </div>
            <span className={`form-step-name${step === s.id ? ' active' : step > s.id ? ' done' : ''}`}>{s.label}</span>
          </div>
        ))}
      </div>

      <div className="form-card">
        {renderStep()}
        {error && (
          <div className="form-error">
            <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0 }}>
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}
        <div className="form-nav">
          {step > 1 ? <button type="button" className="f-btn f-btn-ghost" onClick={back}>← Back</button> : <div />}
          {step < STEPS.length
            ? <button type="button" className="f-btn f-btn-primary" onClick={next}>Continue →</button>
            : <button type="button" className="f-btn f-btn-submit" onClick={submit} disabled={submitting}>
                {submitting ? <><span className="spin" /> Submitting…</> : 'Submit Application'}
              </button>}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   INSURANCE TYPE PICKER
   ════════════════════════════════════════════════════════════════ */
function InsuranceTypePicker({ onSelect, onCancel }) {
  const TYPES = [
    {
      id: 'medical',
      label: 'Medical Insurance',
      desc: 'Health coverage for you and your family members — hospital, ambulatory, specialist, and drug benefits.',
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
        </svg>
      ),
    },
    {
      id: 'car',
      label: 'Car Insurance',
      desc: 'Vehicle protection against accidents, theft, third-party liability, and physical damage.',
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 17H3a2 2 0 01-2-2v-4a2 2 0 012-2h16a2 2 0 012 2v4a2 2 0 01-2 2h-2"/>
          <rect x="7" y="14" width="10" height="7" rx="1"/>
          <path d="M5 9l2-6h10l2 6"/>
          <circle cx="7.5" cy="17.5" r="1.5"/>
          <circle cx="16.5" cy="17.5" r="1.5"/>
        </svg>
      ),
    },
    {
      id: 'property',
      label: 'Property Insurance',
      desc: 'Coverage for your home, apartment, or commercial property against fire, flood, and other risks.',
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
      ),
    },
    {
      id: 'life',
      label: 'Life Insurance',
      desc: "Financial protection and peace of mind for your family's future through term or whole-life coverage.",
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 21C12 21 3 14 3 8a5 5 0 0110 0 5 5 0 0110 0c0 6-9 13-9 13z" strokeLinejoin="round"/>
        </svg>
      ),
    },
  ];

  return (
    <div className="type-picker-shell">
      <div className="type-picker-header">
        <div className="type-picker-kicker">New Application</div>
        <h1 className="type-picker-title">What type of coverage are you applying for?</h1>
        <p className="type-picker-sub">Select the insurance product that best matches your needs. Each form is tailored to the specific information required for that coverage type.</p>
      </div>
      <div className="type-picker-grid">
        {TYPES.map(t => (
          <button key={t.id} className="type-card" onClick={() => onSelect(t.id)}>
            <div className="type-card-icon">{t.icon}</div>
            <div className="type-card-body">
              <div className="type-card-label">{t.label}</div>
              <div className="type-card-desc">{t.desc}</div>
            </div>
            <div className="type-card-chevron">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M9 18l6-6-6-6"/></svg>
            </div>
          </button>
        ))}
      </div>
      <button className="type-picker-cancel" onClick={onCancel}>← Back to dashboard</button>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   SHARED: simple multi-step form shell
   ════════════════════════════════════════════════════════════════ */
function SimpleFormShell({ kicker, title, subtitle, steps, step, progressPct, error, submitting, onBack, onNext, onSubmit, children }) {
  return (
    <div className="form-shell">
      <div className="form-hero">
        <div>
          <div className="form-kicker">{kicker}</div>
          <h1 className="form-hero-title">{title}</h1>
          <p className="form-hero-copy">{subtitle}</p>
        </div>
        <div className="form-hero-side">
          <span className="form-hero-step">Step {step}</span>
          <span className="form-hero-total">of {steps.length}</span>
        </div>
      </div>

      <div className="form-progress">
        <div className="form-progress-line">
          <div className="form-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        {steps.map(s => (
          <div key={s.id} className="form-step-item">
            <div className={`form-step-bubble${step === s.id ? ' active' : step > s.id ? ' done' : ''}`}>
              {step > s.id
                ? <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                : s.id}
            </div>
            <span className={`form-step-name${step === s.id ? ' active' : step > s.id ? ' done' : ''}`}>{s.label}</span>
          </div>
        ))}
      </div>

      <div className="form-card">
        {children}
        {error && (
          <div className="form-error">
            <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0 }}>
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}
        <div className="form-nav">
          {step > 1 ? <button type="button" className="f-btn f-btn-ghost" onClick={onBack}>← Back</button> : <div />}
          {step < steps.length
            ? <button type="button" className="f-btn f-btn-primary" onClick={onNext}>Continue →</button>
            : <button type="button" className="f-btn f-btn-submit" onClick={onSubmit} disabled={submitting}>
                {submitting ? <><span className="spin" /> Submitting…</> : 'Submit Application'}
              </button>}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   CAR INSURANCE FORM
   ════════════════════════════════════════════════════════════════ */
function CarInsuranceForm({ user, onSubmitted }) {
  const [step, setStep]           = useState(1);
  const [error, setError]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [declared, setDeclared]   = useState(false);
  const [signDate, setSignDate]   = useState(todayStr());
  const [signCity, setSignCity]   = useState('');

  const [v, setV] = useState({
    vehicle_type: 'Sedan', make: '', model: '', year: '', vin: '', color: '', value: '',
  });
  const upV = (k, val) => setV(prev => ({ ...prev, [k]: val }));

  const [d, setD] = useState({
    first_name: '', last_name: '',
    gender: 'M',
    dob_day: '', dob_month: '', dob_year: '',
    license_number: '', years_driving: '',
    annual_mileage: '', usage: 'Personal', parking: 'Garage',
    accidents: '0', violations: '0',
    email: user.email, phone: user.phone || '',
  });
  const upD = (k, val) => setD(prev => ({ ...prev, [k]: val }));

  const validate = () => {
    setError('');
    if (step === 1) {
      if (!v.make.trim() || !v.model.trim()) return setError('Vehicle make and model are required.') || false;
      const yr = parseInt(v.year, 10);
      if (!v.year || isNaN(yr) || yr < 1980 || yr > new Date().getFullYear() + 1)
        return setError('Please enter a valid vehicle year.') || false;
      if (!v.value || isNaN(parseFloat(v.value)) || parseFloat(v.value) <= 0)
        return setError('Please enter the estimated vehicle value.') || false;
    }
    if (step === 2) {
      if (!d.first_name.trim() || !d.last_name.trim()) return setError('Driver full name is required.') || false;
      if (!d.dob_day || !d.dob_month || !d.dob_year) return setError('Driver date of birth is required.') || false;
      if (!d.license_number.trim()) return setError('Driver license number is required.') || false;
    }
    return true;
  };

  const next = () => { if (validate()) { setStep(s => s + 1); window.scrollTo(0, 0); } };
  const back = () => { setError(''); setStep(s => s - 1); window.scrollTo(0, 0); };

  const submit = async () => {
    if (!declared) return setError('You must accept the declaration before submitting.');
    setSubmitting(true); setError('');
    try {
      const payload = {
        insurance_type: 'car',
        applicant: {
          name:             `${d.first_name} ${d.last_name}`,
          age:              new Date().getFullYear() - parseInt(d.dob_year || '0', 10),
          gender:           d.gender === 'F' ? 'Female' : 'Male',
          occupation:       'Not specified',
          income:           0,
          health_status:    'Good',
          smoker:           false,
          previous_claims:  parseInt(d.accidents || '0', 10),
          marital_status:   'Single',
          dependents:       0,
          family_history:   { heart_disease: false, diabetes: false, cancer: false },
          plans:            ['Car Insurance'],
          date_of_birth:    `${d.dob_year}-${String(d.dob_month).padStart(2,'0')}-${String(d.dob_day).padStart(2,'0')}`,
          email:            d.email,
          phone:            d.phone,
          mobile:           d.phone,
          address:          '',
          members:          [],
          medical_history_flags: [],
          declaration_date: signDate,
          declaration_city: signCity,
        },
        vehicle: {
          type:   v.vehicle_type,
          make:   v.make,
          model:  v.model,
          year:   parseInt(v.year, 10),
          vin:    v.vin,
          color:  v.color,
          value:  parseFloat(v.value),
        },
        driver: {
          license_number:          d.license_number,
          years_driving:           parseInt(d.years_driving || '0', 10),
          annual_mileage_km:       parseInt(d.annual_mileage || '0', 10),
          primary_use:             d.usage,
          usual_parking:           d.parking,
          accidents_last_5_years:  parseInt(d.accidents, 10),
          violations_last_5_years: parseInt(d.violations, 10),
        },
      };

      const resp = await fetch(`${API_URL}/submit-application`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || `HTTP ${resp.status}`);

      onSubmitted({
        applicant_id:   data.applicant_id,
        submitted_at:   new Date().toISOString(),
        applicant_name: `${d.first_name} ${d.last_name}`,
        plan:           ['Car Insurance'],
        status:         'under_review',
        pdf_parsed:     false,
        email:          d.email,
        members:        [],
        members_count:  0,
      });
    } catch (e) {
      setError(e.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = ((step - 1) / (STEPS_CAR.length - 1)) * 100;

  const renderStep = () => {
    if (step === 1) return (
      <>
        <div className="form-section-head">
          <div className="form-section-tag">Section A</div>
          <h2 className="form-section-title">Vehicle Information</h2>
          <p className="form-section-desc">Tell us about the vehicle you want to insure.</p>
        </div>
        <div className="fg-2">
          <FieldBlock label="Vehicle Type">
            <select className="field-input" value={v.vehicle_type} onChange={e => upV('vehicle_type', e.target.value)}>
              {['Sedan','SUV','Truck','Van','Motorcycle','Coupe','Convertible','Hatchback'].map(t => <option key={t}>{t}</option>)}
            </select>
          </FieldBlock>
          <FieldBlock label="Estimated Value (USD)" req>
            <input className="field-input" type="number" placeholder="e.g. 25000" min="0" value={v.value} onChange={e => upV('value', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Make" req>
            <input className="field-input" placeholder="e.g. Toyota" value={v.make} onChange={e => upV('make', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Model" req>
            <input className="field-input" placeholder="e.g. Camry" value={v.model} onChange={e => upV('model', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Year" req>
            <input className="field-input" type="number" placeholder="e.g. 2021" min="1980" max={new Date().getFullYear() + 1} value={v.year} onChange={e => upV('year', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Color">
            <input className="field-input" placeholder="e.g. Silver" value={v.color} onChange={e => upV('color', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="VIN / Chassis Number" full>
            <input className="field-input" placeholder="Optional" value={v.vin} onChange={e => upV('vin', e.target.value)} />
          </FieldBlock>
        </div>
      </>
    );

    if (step === 2) return (
      <>
        <div className="form-section-head">
          <div className="form-section-tag">Section B</div>
          <h2 className="form-section-title">Driver Details &amp; History</h2>
          <p className="form-section-desc">Information about the primary driver and their driving record.</p>
        </div>
        <div className="fg-2">
          <FieldBlock label="First Name" req>
            <input className="field-input" value={d.first_name} onChange={e => upD('first_name', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Last Name" req>
            <input className="field-input" value={d.last_name} onChange={e => upD('last_name', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Gender">
            <div className="seg-row">
              {[['M','Male'],['F','Female']].map(([val, lbl]) => (
                <button key={val} type="button" className={`seg-btn${d.gender === val ? ' on' : ''}`} onClick={() => upD('gender', val)}>{lbl}</button>
              ))}
            </div>
          </FieldBlock>
          <FieldBlock label="Email">
            <input className="field-input" type="email" value={d.email} onChange={e => upD('email', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Date of Birth - Day" req>
            <input className="field-input" type="number" placeholder="DD" min="1" max="31" value={d.dob_day} onChange={e => upD('dob_day', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Month">
            <input className="field-input" type="number" placeholder="MM" min="1" max="12" value={d.dob_month} onChange={e => upD('dob_month', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Year">
            <input className="field-input" type="number" placeholder="YYYY" min="1920" max="2010" value={d.dob_year} onChange={e => upD('dob_year', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="License Number" req>
            <input className="field-input" value={d.license_number} onChange={e => upD('license_number', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Years of Driving Experience">
            <input className="field-input" type="number" min="0" max="60" placeholder="e.g. 5" value={d.years_driving} onChange={e => upD('years_driving', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Annual Mileage (km)">
            <input className="field-input" type="number" min="0" placeholder="e.g. 15000" value={d.annual_mileage} onChange={e => upD('annual_mileage', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Primary Use">
            <select className="field-input" value={d.usage} onChange={e => upD('usage', e.target.value)}>
              <option>Personal</option>
              <option>Commercial</option>
              <option>Business</option>
            </select>
          </FieldBlock>
          <FieldBlock label="Usual Parking">
            <select className="field-input" value={d.parking} onChange={e => upD('parking', e.target.value)}>
              <option>Garage</option>
              <option>Covered Parking</option>
              <option>Open Street</option>
            </select>
          </FieldBlock>
          <FieldBlock label="Accidents in Last 5 Years">
            <select className="field-input" value={d.accidents} onChange={e => upD('accidents', e.target.value)}>
              <option value="0">None</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3 or more</option>
            </select>
          </FieldBlock>
          <FieldBlock label="Traffic Violations in Last 5 Years">
            <select className="field-input" value={d.violations} onChange={e => upD('violations', e.target.value)}>
              <option value="0">None</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3 or more</option>
            </select>
          </FieldBlock>
          <FieldBlock label="Phone">
            <input className="field-input" value={d.phone} onChange={e => upD('phone', e.target.value)} />
          </FieldBlock>
        </div>
      </>
    );

    if (step === 3) return (
      <>
        <div className="form-section-head">
          <div className="form-section-tag">Section C</div>
          <h2 className="form-section-title">Declaration &amp; Review</h2>
          <p className="form-section-desc">Review your application summary and accept the declaration before submitting.</p>
        </div>
        <div className="review-table" style={{ marginBottom: 14 }}>
          <div className="review-table-head">Application Summary</div>
          {[
            ['Vehicle',         `${v.year} ${v.make} ${v.model} (${v.vehicle_type})`],
            ['Estimated Value', `$${parseFloat(v.value || 0).toLocaleString()}`],
            ['Driver',          `${d.first_name} ${d.last_name}`],
            ['Annual Mileage',  d.annual_mileage ? `${parseInt(d.annual_mileage).toLocaleString()} km` : 'Not specified'],
            ['Primary Use',     d.usage],
            ['Accidents (5yr)', d.accidents === '0' ? 'None' : d.accidents],
            ['Violations (5yr)',d.violations === '0' ? 'None' : d.violations],
          ].map(([k, val]) => (
            <div key={k} className="review-row">
              <div className="review-key">{k}</div>
              <div className="review-val">{val}</div>
            </div>
          ))}
        </div>
        <div className="declaration-box">
          I, the undersigned, hereby declare that all statements made in this proposal form are true, complete, and accurate to the best of my knowledge and belief. I understand that any misrepresentation, omission, or concealment of facts may result in the rejection of the application or cancellation of the policy.
        </div>
        <div className="decl-check-row" onClick={() => setDeclared(prev => !prev)}>
          <input type="checkbox" className="decl-checkbox" checked={declared} onChange={() => setDeclared(prev => !prev)} />
          <span className="decl-check-text">I have read and understood the declaration above and confirm all information provided is true and complete.</span>
        </div>
        <div className="fg-2">
          <div className="field-block">
            <label className="field-label">Date<span className="field-required">*</span></label>
            <input className="field-input" value={signDate} onChange={e => setSignDate(e.target.value)} placeholder="DD/MM/YYYY" />
          </div>
          <div className="field-block">
            <label className="field-label">Place (City)</label>
            <input className="field-input" value={signCity} onChange={e => setSignCity(e.target.value)} placeholder="City" />
          </div>
        </div>
      </>
    );
    return null;
  };

  return (
    <SimpleFormShell
      kicker="Car Insurance Application"
      title="Vehicle coverage made simple."
      subtitle="Provide your vehicle details and driver information to receive a tailored car insurance quote."
      steps={STEPS_CAR} step={step} progressPct={progressPct}
      error={error} submitting={submitting}
      onBack={back} onNext={next} onSubmit={submit}
    >
      {renderStep()}
    </SimpleFormShell>
  );
}

/* ════════════════════════════════════════════════════════════════
   PROPERTY INSURANCE FORM
   ════════════════════════════════════════════════════════════════ */
function PropertyInsuranceForm({ user, onSubmitted }) {
  const [step, setStep]           = useState(1);
  const [error, setError]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [declared, setDeclared]   = useState(false);
  const [signDate, setSignDate]   = useState(todayStr());
  const [signCity, setSignCity]   = useState('');

  const [p, setP] = useState({
    property_type: 'Apartment', address: '', city: '', ownership: 'Own',
    value: '', year_built: '', size_sqm: '', construction: 'Reinforced Concrete',
  });
  const upP = (k, val) => setP(prev => ({ ...prev, [k]: val }));

  const [c, setC] = useState({
    coverage_type: 'All-Risk', coverage_amount: '',
    security_system: 'No', fire_alarm: 'No',
    prior_claims: 'No', prior_claims_count: '0',
  });
  const upC = (k, val) => setC(prev => ({ ...prev, [k]: val }));

  const validate = () => {
    setError('');
    if (step === 1) {
      if (!p.address.trim() || !p.city.trim()) return setError('Property address and city are required.') || false;
      if (!p.value || isNaN(parseFloat(p.value)) || parseFloat(p.value) <= 0)
        return setError('Please enter a valid property value.') || false;
    }
    if (step === 2) {
      if (!c.coverage_amount || isNaN(parseFloat(c.coverage_amount)) || parseFloat(c.coverage_amount) <= 0)
        return setError('Please enter the desired coverage amount.') || false;
    }
    return true;
  };

  const next = () => { if (validate()) { setStep(s => s + 1); window.scrollTo(0, 0); } };
  const back = () => { setError(''); setStep(s => s - 1); window.scrollTo(0, 0); };

  const submit = async () => {
    if (!declared) return setError('You must accept the declaration before submitting.');
    setSubmitting(true); setError('');
    try {
      const payload = {
        insurance_type: 'property',
        applicant: {
          name:             user.name,
          age:              0,
          gender:           'Not specified',
          occupation:       'Not specified',
          income:           0,
          health_status:    'Good',
          smoker:           false,
          previous_claims:  c.prior_claims === 'Yes' ? parseInt(c.prior_claims_count || '1', 10) : 0,
          marital_status:   'Not specified',
          dependents:       0,
          family_history:   { heart_disease: false, diabetes: false, cancer: false },
          plans:            ['Property Insurance'],
          email:            user.email,
          phone:            user.phone || '',
          mobile:           user.phone || '',
          address:          `${p.address}, ${p.city}`,
          members:          [],
          medical_history_flags: [],
          declaration_date: signDate,
          declaration_city: signCity,
        },
        property: {
          type:          p.property_type,
          address:       p.address,
          city:          p.city,
          ownership:     p.ownership,
          value:         parseFloat(p.value),
          year_built:    parseInt(p.year_built || '0', 10),
          size_sqm:      parseFloat(p.size_sqm || '0'),
          construction:  p.construction,
        },
        coverage: {
          type:             c.coverage_type,
          amount:           parseFloat(c.coverage_amount),
          security_system:  c.security_system === 'Yes',
          fire_alarm:       c.fire_alarm === 'Yes',
          prior_claims:     c.prior_claims === 'Yes',
          prior_claims_count: parseInt(c.prior_claims_count || '0', 10),
        },
      };

      const resp = await fetch(`${API_URL}/submit-application`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || `HTTP ${resp.status}`);

      onSubmitted({
        applicant_id:   data.applicant_id,
        submitted_at:   new Date().toISOString(),
        applicant_name: user.name,
        plan:           ['Property Insurance'],
        status:         'under_review',
        pdf_parsed:     false,
        email:          user.email,
        members:        [],
        members_count:  0,
      });
    } catch (e) {
      setError(e.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = ((step - 1) / (STEPS_PROPERTY.length - 1)) * 100;

  const renderStep = () => {
    if (step === 1) return (
      <>
        <div className="form-section-head">
          <div className="form-section-tag">Section A</div>
          <h2 className="form-section-title">Property Details</h2>
          <p className="form-section-desc">Tell us about the property you want to insure.</p>
        </div>
        <div className="fg-2">
          <FieldBlock label="Property Type">
            <select className="field-input" value={p.property_type} onChange={e => upP('property_type', e.target.value)}>
              {['Apartment','House','Villa','Commercial Building','Warehouse','Land'].map(t => <option key={t}>{t}</option>)}
            </select>
          </FieldBlock>
          <FieldBlock label="Ownership Status">
            <select className="field-input" value={p.ownership} onChange={e => upP('ownership', e.target.value)}>
              <option>Own</option>
              <option>Rent</option>
              <option>Mortgage</option>
            </select>
          </FieldBlock>
          <FieldBlock label="Street Address" req full>
            <input className="field-input" placeholder="Building, Street, Area" value={p.address} onChange={e => upP('address', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="City" req>
            <input className="field-input" placeholder="e.g. Beirut" value={p.city} onChange={e => upP('city', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Property Value (USD)" req>
            <input className="field-input" type="number" placeholder="e.g. 200000" min="0" value={p.value} onChange={e => upP('value', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Year Built">
            <input className="field-input" type="number" placeholder="e.g. 2005" min="1800" max={new Date().getFullYear()} value={p.year_built} onChange={e => upP('year_built', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Size (sqm)">
            <input className="field-input" type="number" placeholder="e.g. 150" min="0" value={p.size_sqm} onChange={e => upP('size_sqm', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Construction Type">
            <select className="field-input" value={p.construction} onChange={e => upP('construction', e.target.value)}>
              {['Reinforced Concrete','Brick','Stone','Wood','Mixed','Steel Frame'].map(t => <option key={t}>{t}</option>)}
            </select>
          </FieldBlock>
        </div>
      </>
    );

    if (step === 2) return (
      <>
        <div className="form-section-head">
          <div className="form-section-tag">Section B</div>
          <h2 className="form-section-title">Coverage &amp; Risk Assessment</h2>
          <p className="form-section-desc">Select your desired coverage and provide risk information about the property.</p>
        </div>
        <div className="fg-2">
          <FieldBlock label="Coverage Type">
            <select className="field-input" value={c.coverage_type} onChange={e => upC('coverage_type', e.target.value)}>
              {['All-Risk','Fire Only','Fire & Theft','Flood','Earthquake','Comprehensive'].map(t => <option key={t}>{t}</option>)}
            </select>
          </FieldBlock>
          <FieldBlock label="Desired Coverage Amount (USD)" req>
            <input className="field-input" type="number" placeholder="e.g. 150000" min="0" value={c.coverage_amount} onChange={e => upC('coverage_amount', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Security System Installed">
            <div className="seg-row">
              {['Yes','No'].map(opt => (
                <button key={opt} type="button" className={`seg-btn${c.security_system === opt ? ' on' : ''}`} onClick={() => upC('security_system', opt)}>{opt}</button>
              ))}
            </div>
          </FieldBlock>
          <FieldBlock label="Fire Alarm / Sprinkler System">
            <div className="seg-row">
              {['Yes','No'].map(opt => (
                <button key={opt} type="button" className={`seg-btn${c.fire_alarm === opt ? ' on' : ''}`} onClick={() => upC('fire_alarm', opt)}>{opt}</button>
              ))}
            </div>
          </FieldBlock>
          <FieldBlock label="Any Insurance Claims in Last 5 Years">
            <div className="seg-row">
              {['Yes','No'].map(opt => (
                <button key={opt} type="button" className={`seg-btn${c.prior_claims === opt ? ' on' : ''}`} onClick={() => upC('prior_claims', opt)}>{opt}</button>
              ))}
            </div>
          </FieldBlock>
          {c.prior_claims === 'Yes' && (
            <FieldBlock label="Number of Prior Claims">
              <select className="field-input" value={c.prior_claims_count} onChange={e => upC('prior_claims_count', e.target.value)}>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4 or more</option>
              </select>
            </FieldBlock>
          )}
        </div>
      </>
    );

    if (step === 3) return (
      <>
        <div className="form-section-head">
          <div className="form-section-tag">Section C</div>
          <h2 className="form-section-title">Declaration &amp; Review</h2>
          <p className="form-section-desc">Review your application summary and accept the declaration before submitting.</p>
        </div>
        <div className="review-table" style={{ marginBottom: 14 }}>
          <div className="review-table-head">Application Summary</div>
          {[
            ['Property Type',    p.property_type],
            ['Address',         `${p.address}, ${p.city}`],
            ['Ownership',       p.ownership],
            ['Property Value',  `$${parseFloat(p.value || 0).toLocaleString()}`],
            ['Construction',    p.construction],
            ['Coverage Type',   c.coverage_type],
            ['Coverage Amount', `$${parseFloat(c.coverage_amount || 0).toLocaleString()}`],
            ['Security System', c.security_system],
            ['Prior Claims',    c.prior_claims],
          ].map(([k, val]) => (
            <div key={k} className="review-row">
              <div className="review-key">{k}</div>
              <div className="review-val">{val}</div>
            </div>
          ))}
        </div>
        <div className="declaration-box">
          I, the undersigned, hereby declare that all statements made in this proposal form are true, complete, and accurate to the best of my knowledge and belief. I understand that any misrepresentation or concealment of facts may result in the rejection of the application or cancellation of the policy.
        </div>
        <div className="decl-check-row" onClick={() => setDeclared(prev => !prev)}>
          <input type="checkbox" className="decl-checkbox" checked={declared} onChange={() => setDeclared(prev => !prev)} />
          <span className="decl-check-text">I have read and understood the declaration above and confirm all information provided is true and complete.</span>
        </div>
        <div className="fg-2">
          <div className="field-block">
            <label className="field-label">Date<span className="field-required">*</span></label>
            <input className="field-input" value={signDate} onChange={e => setSignDate(e.target.value)} placeholder="DD/MM/YYYY" />
          </div>
          <div className="field-block">
            <label className="field-label">Place (City)</label>
            <input className="field-input" value={signCity} onChange={e => setSignCity(e.target.value)} placeholder="City" />
          </div>
        </div>
      </>
    );
    return null;
  };

  return (
    <SimpleFormShell
      kicker="Property Insurance Application"
      title="Protect what matters most."
      subtitle="Provide your property details and desired coverage to receive a tailored property insurance quote."
      steps={STEPS_PROPERTY} step={step} progressPct={progressPct}
      error={error} submitting={submitting}
      onBack={back} onNext={next} onSubmit={submit}
    >
      {renderStep()}
    </SimpleFormShell>
  );
}

/* ════════════════════════════════════════════════════════════════
   LIFE INSURANCE FORM
   ════════════════════════════════════════════════════════════════ */
function LifeInsuranceForm({ user, onSubmitted }) {
  const [step, setStep]           = useState(1);
  const [error, setError]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [declared, setDeclared]   = useState(false);
  const [signDate, setSignDate]   = useState(todayStr());
  const [signCity, setSignCity]   = useState('');

  const [c, setC] = useState({
    coverage_amount: '', coverage_type: 'Term Life', term_years: '20',
    beneficiary_name: '', beneficiary_relationship: 'Spouse',
  });
  const upC = (k, val) => setC(prev => ({ ...prev, [k]: val }));

  const [h, setH] = useState({
    height_cm: '', weight_kg: '', smoker: 'No',
    on_medication: 'No', medication_details: '',
    chronic_conditions: [],
    family_diabetes: false, family_heart: false, family_cancer: false, family_hypertension: false,
  });
  const upH = (k, val) => setH(prev => ({ ...prev, [k]: val }));
  const toggleCondition = (cond) => setH(prev => ({
    ...prev,
    chronic_conditions: prev.chronic_conditions.includes(cond)
      ? prev.chronic_conditions.filter(x => x !== cond)
      : [...prev.chronic_conditions, cond],
  }));

  const bmi = (h.height_cm && h.weight_kg)
    ? (parseFloat(h.weight_kg) / Math.pow(parseFloat(h.height_cm) / 100, 2)).toFixed(1)
    : null;

  const validate = () => {
    setError('');
    if (step === 1) {
      if (!c.coverage_amount || isNaN(parseFloat(c.coverage_amount)) || parseFloat(c.coverage_amount) <= 0)
        return setError('Please enter a valid coverage amount.') || false;
      if (!c.beneficiary_name.trim()) return setError('Beneficiary name is required.') || false;
    }
    return true;
  };

  const next = () => { if (validate()) { setStep(s => s + 1); window.scrollTo(0, 0); } };
  const back = () => { setError(''); setStep(s => s - 1); window.scrollTo(0, 0); };

  const submit = async () => {
    if (!declared) return setError('You must accept the declaration before submitting.');
    setSubmitting(true); setError('');
    try {
      const smoker = h.smoker === 'Yes';
      const conditions = h.chronic_conditions;
      const familyHistory = {
        heart_disease:   h.family_heart,
        diabetes:        h.family_diabetes,
        cancer:          h.family_cancer,
        hypertension:    h.family_hypertension,
      };

      const payload = {
        insurance_type: 'life',
        applicant: {
          name:             user.name,
          age:              0,
          gender:           'Not specified',
          occupation:       'Not specified',
          income:           0,
          health_status:    conditions.length === 0 ? 'Good' : conditions.length <= 2 ? 'Fair' : 'Poor',
          smoker,
          previous_claims:  0,
          marital_status:   'Not specified',
          dependents:       0,
          family_history:   familyHistory,
          plans:            ['Life Insurance'],
          email:            user.email,
          phone:            user.phone || '',
          mobile:           user.phone || '',
          address:          '',
          members:          [],
          medical_history_flags: [],
          declaration_date: signDate,
          declaration_city: signCity,
        },
        life_coverage: {
          amount:                   parseFloat(c.coverage_amount),
          type:                     c.coverage_type,
          term_years:               c.coverage_type === 'Term Life' ? parseInt(c.term_years, 10) : null,
          beneficiary_name:         c.beneficiary_name,
          beneficiary_relationship: c.beneficiary_relationship,
        },
        health: {
          height_cm:          parseFloat(h.height_cm || '0'),
          weight_kg:          parseFloat(h.weight_kg || '0'),
          bmi:                bmi ? parseFloat(bmi) : null,
          smoker,
          on_medication:      h.on_medication === 'Yes',
          medication_details: h.medication_details,
          chronic_conditions: conditions,
          family_history:     familyHistory,
        },
      };

      const resp = await fetch(`${API_URL}/submit-application`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || `HTTP ${resp.status}`);

      onSubmitted({
        applicant_id:   data.applicant_id,
        submitted_at:   new Date().toISOString(),
        applicant_name: user.name,
        plan:           ['Life Insurance'],
        status:         'under_review',
        pdf_parsed:     false,
        email:          user.email,
        members:        [],
        members_count:  0,
      });
    } catch (e) {
      setError(e.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = ((step - 1) / (STEPS_LIFE.length - 1)) * 100;
  const CHRONIC_OPTIONS = ['Diabetes','Hypertension','Heart Disease','Asthma','Cancer','Kidney Disease','Liver Disease','None'];

  const renderStep = () => {
    if (step === 1) return (
      <>
        <div className="form-section-head">
          <div className="form-section-tag">Section A</div>
          <h2 className="form-section-title">Coverage &amp; Beneficiary</h2>
          <p className="form-section-desc">Select your life insurance coverage type, amount, and designate your beneficiary.</p>
        </div>
        <div className="fg-2">
          <FieldBlock label="Coverage Type">
            <select className="field-input" value={c.coverage_type} onChange={e => upC('coverage_type', e.target.value)}>
              <option>Term Life</option>
              <option>Whole Life</option>
              <option>Universal Life</option>
            </select>
          </FieldBlock>
          {c.coverage_type === 'Term Life' && (
            <FieldBlock label="Term Length (Years)">
              <select className="field-input" value={c.term_years} onChange={e => upC('term_years', e.target.value)}>
                {['10','15','20','25','30'].map(y => <option key={y}>{y}</option>)}
              </select>
            </FieldBlock>
          )}
          <FieldBlock label="Coverage Amount (USD)" req>
            <input className="field-input" type="number" placeholder="e.g. 500000" min="0" value={c.coverage_amount} onChange={e => upC('coverage_amount', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Beneficiary Full Name" req>
            <input className="field-input" placeholder="Full name" value={c.beneficiary_name} onChange={e => upC('beneficiary_name', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Beneficiary Relationship">
            <select className="field-input" value={c.beneficiary_relationship} onChange={e => upC('beneficiary_relationship', e.target.value)}>
              {['Spouse','Child','Parent','Sibling','Other'].map(r => <option key={r}>{r}</option>)}
            </select>
          </FieldBlock>
        </div>
      </>
    );

    if (step === 2) return (
      <>
        <div className="form-section-head">
          <div className="form-section-tag">Section B</div>
          <h2 className="form-section-title">Health Declaration</h2>
          <p className="form-section-desc">Please answer all health questions honestly. This information is used to assess your coverage eligibility and premium.</p>
        </div>
        <div className="fg-2">
          <FieldBlock label="Height (cm)">
            <input className="field-input" type="number" placeholder="e.g. 175" min="100" max="250" value={h.height_cm} onChange={e => upH('height_cm', e.target.value)} />
          </FieldBlock>
          <FieldBlock label="Weight (kg)">
            <input className="field-input" type="number" placeholder="e.g. 75" min="30" max="300" value={h.weight_kg} onChange={e => upH('weight_kg', e.target.value)} />
          </FieldBlock>
          {bmi && (
            <FieldBlock label="BMI (calculated)" full>
              <div className="field-input" style={{ display: 'flex', alignItems: 'center', cursor: 'default' }}>
                <strong>{bmi}</strong>&nbsp;
                <span style={{ color: '#6b7280', fontSize: 13 }}>
                  {parseFloat(bmi) < 18.5 ? '— Underweight' : parseFloat(bmi) < 25 ? '— Normal weight' : parseFloat(bmi) < 30 ? '— Overweight' : '— Obese'}
                </span>
              </div>
            </FieldBlock>
          )}
          <FieldBlock label="Smoker">
            <div className="seg-row">
              {['Yes','No'].map(opt => (
                <button key={opt} type="button" className={`seg-btn${h.smoker === opt ? ' on' : ''}`} onClick={() => upH('smoker', opt)}>{opt}</button>
              ))}
            </div>
          </FieldBlock>
          <FieldBlock label="Currently Taking Regular Medication">
            <div className="seg-row">
              {['Yes','No'].map(opt => (
                <button key={opt} type="button" className={`seg-btn${h.on_medication === opt ? ' on' : ''}`} onClick={() => upH('on_medication', opt)}>{opt}</button>
              ))}
            </div>
          </FieldBlock>
          {h.on_medication === 'Yes' && (
            <FieldBlock label="Medication Details" full>
              <input className="field-input" placeholder="List medications and conditions" value={h.medication_details} onChange={e => upH('medication_details', e.target.value)} />
            </FieldBlock>
          )}
          <FieldBlock label="Chronic Conditions (select all that apply)" full>
            <div className="condition-check-grid">
              {CHRONIC_OPTIONS.map(cond => (
                <label key={cond} className={`condition-chip${h.chronic_conditions.includes(cond) ? ' selected' : ''}`}>
                  <input type="checkbox" checked={h.chronic_conditions.includes(cond)} onChange={() => toggleCondition(cond)} />
                  {cond}
                </label>
              ))}
            </div>
          </FieldBlock>
          <FieldBlock label="Family History — Diabetes">
            <div className="seg-row">
              {['Yes','No'].map(opt => (
                <button key={opt} type="button" className={`seg-btn${(h.family_diabetes ? 'Yes' : 'No') === opt ? ' on' : ''}`} onClick={() => upH('family_diabetes', opt === 'Yes')}>{opt}</button>
              ))}
            </div>
          </FieldBlock>
          <FieldBlock label="Family History — Heart Disease">
            <div className="seg-row">
              {['Yes','No'].map(opt => (
                <button key={opt} type="button" className={`seg-btn${(h.family_heart ? 'Yes' : 'No') === opt ? ' on' : ''}`} onClick={() => upH('family_heart', opt === 'Yes')}>{opt}</button>
              ))}
            </div>
          </FieldBlock>
          <FieldBlock label="Family History — Cancer">
            <div className="seg-row">
              {['Yes','No'].map(opt => (
                <button key={opt} type="button" className={`seg-btn${(h.family_cancer ? 'Yes' : 'No') === opt ? ' on' : ''}`} onClick={() => upH('family_cancer', opt === 'Yes')}>{opt}</button>
              ))}
            </div>
          </FieldBlock>
          <FieldBlock label="Family History — Hypertension">
            <div className="seg-row">
              {['Yes','No'].map(opt => (
                <button key={opt} type="button" className={`seg-btn${(h.family_hypertension ? 'Yes' : 'No') === opt ? ' on' : ''}`} onClick={() => upH('family_hypertension', opt === 'Yes')}>{opt}</button>
              ))}
            </div>
          </FieldBlock>
        </div>
      </>
    );

    if (step === 3) return (
      <>
        <div className="form-section-head">
          <div className="form-section-tag">Section C</div>
          <h2 className="form-section-title">Declaration &amp; Review</h2>
          <p className="form-section-desc">Review your application summary and accept the declaration before submitting.</p>
        </div>
        <div className="review-table" style={{ marginBottom: 14 }}>
          <div className="review-table-head">Application Summary</div>
          {[
            ['Coverage Type',    c.coverage_type + (c.coverage_type === 'Term Life' ? ` (${c.term_years} years)` : '')],
            ['Coverage Amount',  `$${parseFloat(c.coverage_amount || 0).toLocaleString()}`],
            ['Beneficiary',     `${c.beneficiary_name} (${c.beneficiary_relationship})`],
            ['BMI',              bmi ? `${bmi} kg/m²` : 'Not provided'],
            ['Smoker',           h.smoker],
            ['On Medication',    h.on_medication],
            ['Chronic Conditions', h.chronic_conditions.length ? h.chronic_conditions.join(', ') : 'None declared'],
          ].map(([k, val]) => (
            <div key={k} className="review-row">
              <div className="review-key">{k}</div>
              <div className="review-val">{val}</div>
            </div>
          ))}
        </div>
        <div className="declaration-box">
          I, the undersigned, hereby declare that all statements made in this proposal form are true, complete, and accurate to the best of my knowledge and belief. I understand that concealment or misrepresentation of health information may invalidate this policy. I authorize the insurance company to obtain any medical information necessary to assess this application.
        </div>
        <div className="decl-check-row" onClick={() => setDeclared(prev => !prev)}>
          <input type="checkbox" className="decl-checkbox" checked={declared} onChange={() => setDeclared(prev => !prev)} />
          <span className="decl-check-text">I have read and understood the declaration above and confirm all health information provided is true and complete.</span>
        </div>
        <div className="fg-2">
          <div className="field-block">
            <label className="field-label">Date<span className="field-required">*</span></label>
            <input className="field-input" value={signDate} onChange={e => setSignDate(e.target.value)} placeholder="DD/MM/YYYY" />
          </div>
          <div className="field-block">
            <label className="field-label">Place (City)</label>
            <input className="field-input" value={signCity} onChange={e => setSignCity(e.target.value)} placeholder="City" />
          </div>
        </div>
      </>
    );
    return null;
  };

  return (
    <SimpleFormShell
      kicker="Life Insurance Application"
      title="Secure your family's future."
      subtitle="Provide your coverage preferences and health declaration to receive a tailored life insurance quote."
      steps={STEPS_LIFE} step={step} progressPct={progressPct}
      error={error} submitting={submitting}
      onBack={back} onNext={next} onSubmit={submit}
    >
      {renderStep()}
    </SimpleFormShell>
  );
}

/* ════════════════════════════════════════════════════════════════
   STATUS SCREEN
   ════════════════════════════════════════════════════════════════ */
function ApplicantDashboard({ user, applications, onStartApplication, onViewApplication, onSwitchToChat, onFileClaim }) {
  const latestApplication = applications[0] || null;
  const firstName = (user?.name || 'Applicant').split(' ')[0];
  const inReviewCount = applications.filter(application => (application.status || 'under_review') === 'under_review').length;
  const parsedCount = applications.filter(application => application.pdf_parsed).length;
  const latestPlan = latestApplication ? formatPlanDisplay(latestApplication.plan) : 'No plan selected yet';

  return (
    <div className="dashboard-shell">
      <section className="dashboard-hero dashboard-hero-grid">
        <div className="dashboard-hero-main">
          <div className="dashboard-kicker">Applicant Portal</div>
          <h1 className="dashboard-title">Welcome back, {firstName}</h1>
          <p className="dashboard-subtitle">
            Start a new insurance application, check recent submissions, and stay close to the underwriting process from one guided workspace.
          </p>
          <div className="dashboard-hero-actions">
            <button className="dashboard-btn dashboard-btn-primary" onClick={onStartApplication}>
              Start new application
            </button>
            <button
              className="dashboard-btn dashboard-btn-secondary"
              onClick={() => latestApplication ? onViewApplication(latestApplication.applicant_id) : onStartApplication()}
            >
              {latestApplication ? 'Review latest status' : 'Create first application'}
            </button>
          </div>
        </div>

        <div className="dashboard-metrics">
          <article className="dashboard-metric">
            <span>Applications</span>
            <strong>{applications.length}</strong>
            <small>Total records in your portal</small>
          </article>
          <article className="dashboard-metric">
            <span>In review</span>
            <strong>{inReviewCount}</strong>
            <small>Awaiting underwriting decisions</small>
          </article>
          <article className="dashboard-metric">
            <span>Documents parsed</span>
            <strong>{parsedCount}</strong>
            <small>Medical files processed by AI</small>
          </article>
        </div>
      </section>

      <div className="dashboard-grid">
        <section className="dashboard-card dashboard-card-primary">
          <div className="dashboard-card-tag">New application</div>
          <h2 className="dashboard-card-title">Apply for coverage</h2>
          <p className="dashboard-card-copy">
            Open a fresh medical insurance form, add family members, and prepare an underwriting-ready submission.
          </p>
          <button className="dashboard-btn dashboard-btn-primary" onClick={onStartApplication}>
            Start new form
          </button>
        </section>

        <section className="dashboard-support-card">
          <div className="dashboard-support-label">Portfolio snapshot</div>
          <div className="dashboard-support-value">{latestPlan}</div>
          <p className="dashboard-card-copy">
            {latestApplication
              ? `Latest application ID: ${latestApplication.applicant_id}`
              : 'Your next submission will appear here with plan, status, and tracking details.'}
          </p>
          {latestApplication && (
            <div className="dashboard-support-actions">
              <button
                className="dashboard-inline-btn dashboard-inline-btn-secondary"
                onClick={() => onSwitchToChat && onSwitchToChat(latestApplication.applicant_id)}
              >
                Ask AI about latest file
              </button>
            </div>
          )}
        </section>

        <section className="dashboard-card">
          <div className="dashboard-card-tag">Submitted applications</div>
          <h2 className="dashboard-card-title">
            {applications.length
              ? `${applications.length} saved ${applications.length === 1 ? 'application' : 'applications'}`
              : 'No submitted applications yet'}
          </h2>
          <p className="dashboard-card-copy">
            {latestApplication
              ? 'Review the latest status for any submitted application or open a chat about it.'
              : 'Once you submit a form, it will show up here with its current review status.'}
          </p>
          <button
            className="dashboard-btn dashboard-btn-secondary"
            onClick={() => latestApplication ? onViewApplication(latestApplication.applicant_id) : onStartApplication()}
          >
            {latestApplication ? 'View latest status' : 'Create first application'}
          </button>
        </section>
      </div>

      <section className="dashboard-list">
        <div className="dashboard-list-head">
          <div>
            <h2 className="dashboard-list-title">Submitted Applications</h2>
            <p className="dashboard-list-subtitle">Track status, coverage selection, and AI support for every submission.</p>
          </div>
          <span className="dashboard-list-count">{applications.length}</span>
        </div>

        {applications.length ? (
          <div className="dashboard-list-body">
            {applications.map((application) => (
              <article key={application.applicant_id} className="dashboard-app-card">
                <div className="dashboard-app-main">
                  <div className="dashboard-app-id ui-mono">{application.applicant_id}</div>
                  <div className="dashboard-app-name">{application.applicant_name || user.name}</div>
                  <div className="dashboard-app-meta">
                    <span>{new Date(application.submitted_at).toLocaleDateString()}</span>
                    <span>{formatPlanDisplay(application.plan)}</span>
                  </div>
                </div>

                <div className="dashboard-app-side">
                  <span className={`dashboard-status dashboard-status-${application.status || 'under_review'}`}>
                    {formatStatusLabel(application.status)}
                  </span>
                  <div className="dashboard-app-actions">
                    <button
                      className="dashboard-inline-btn"
                      onClick={() => onViewApplication(application.applicant_id)}
                    >
                      View status
                    </button>
                    <button
                      className="dashboard-inline-btn dashboard-inline-btn-secondary"
                      onClick={() => onSwitchToChat && onSwitchToChat(application.applicant_id)}
                    >
                      Ask AI
                    </button>
                    {application.status === 'approved' && (
                      <button
                        className="dashboard-inline-btn dashboard-inline-btn-secondary"
                        onClick={() => onFileClaim && onFileClaim(application.applicant_id)}
                      >
                        File a Claim
                      </button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="dashboard-empty">
            <div className="dashboard-empty-title">No applications submitted yet</div>
            <p className="dashboard-empty-copy">
              Start a new form to track its review status here after submission.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

function StatusScreen({ appRecord: initialRecord, onSwitchToChat, onNewApplication, onBackToDashboard }) {
  const [appRecord, setAppRecord] = useState(initialRecord);

  useEffect(() => {
    // Fetch full application details (includes coverage_details, policy info) for approved/rejected apps
    if (initialRecord.applicant_id && (initialRecord.status === 'approved' || initialRecord.status === 'rejected')) {
      fetch(`${API_URL}/application/${initialRecord.applicant_id}`)
        .then(r => r.json())
        .then(data => {
          if (data.found && data.application) {
            setAppRecord(prev => ({ ...prev, ...data.application, applicant_name: prev.applicant_name || data.application.name }));
          }
        })
        .catch(() => {});
    }
  }, [initialRecord.applicant_id, initialRecord.status]);

  const planDisplay = formatPlanDisplay(appRecord.plan || appRecord.plans);
  const insuredCount = 1 + (appRecord.members?.length || appRecord.members_count || 0);
  const currentStatus = appRecord.status || 'under_review';

  const isApproved = currentStatus === 'approved';
  const isRejected = currentStatus === 'rejected';
  const isDecided = isApproved || isRejected;
  const isPendingInfo = currentStatus === 'pending_info';
  const isEscalated = currentStatus === 'escalated';

  const getStepStatus = (stepIndex) => {
    // Step 0: Application Received — always done
    if (stepIndex === 0) return 'done';
    // Step 1: AI Risk Assessment
    if (stepIndex === 1) {
      if (isDecided || isPendingInfo || isEscalated) return 'done';
      return 'active';
    }
    // Step 2: Underwriter Review
    if (stepIndex === 2) {
      if (isDecided) return 'done';
      if (isPendingInfo || isEscalated) return 'active';
      return 'pending';
    }
    // Step 3: Decision Issued
    if (stepIndex === 3) {
      if (isDecided) return 'done';
      return 'pending';
    }
    return 'pending';
  };

  const steps = [
    { name: 'Application Received', desc: 'Your application has been successfully submitted.', status: getStepStatus(0), time: new Date(appRecord.submitted_at).toLocaleString() },
    { name: 'AI Risk Assessment',   desc: 'Our AI system analyzed your risk profile and medical data.', status: getStepStatus(1), time: getStepStatus(1) === 'done' ? 'Complete' : 'In progress…' },
    { name: 'Underwriter Review',   desc: isPendingInfo ? 'Additional information has been requested.' : isEscalated ? 'Your case has been escalated for senior review.' : 'A licensed underwriter reviewed the AI assessment.', status: getStepStatus(2), time: getStepStatus(2) === 'done' ? 'Complete' : getStepStatus(2) === 'active' ? 'In progress…' : 'Estimated: 1–2 business days' },
    { name: 'Decision Issued',      desc: isApproved ? 'Your application has been approved! You will receive your policy details by email.' : isRejected ? 'After careful review, your application was not approved at this time.' : 'You will be notified once a final decision has been made.', status: getStepStatus(3), time: isDecided ? 'Decision final' : 'Estimated: 2–3 business days' },
  ];

  const heroIcon = isApproved
    ? <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="8 12 11 15 16 9" /></svg>
    : isRejected
    ? <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
    : <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;

  const heroTitle = isApproved
    ? 'Application approved'
    : isRejected
    ? 'Application not approved'
    : isPendingInfo
    ? 'Additional information requested'
    : isEscalated
    ? 'Under senior review'
    : 'Application under review';

  const heroSub = isApproved
    ? 'Congratulations! Your insurance application has been approved. See your coverage details below.'
    : isRejected
    ? 'After careful evaluation, we were unable to approve your application at this time.'
    : isPendingInfo
    ? 'The underwriting team needs additional information to complete your review. Please check your email for details.'
    : isEscalated
    ? 'Your application requires additional clinical review. A senior underwriter is reviewing your case.'
    : 'Your insurance application has been received. Our team will review it within 2–3 business days.';

  const handleDownloadPdf = async () => {
    try {
      const resp = await fetch(`${API_URL}/policy-report/${appRecord.applicant_id}`);
      const data = await resp.json();
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (e) {
      console.error('Failed to download PDF:', e);
    }
  };

  return (
    <div className="status-screen">
      <div className="status-hero">
        <div className="status-icon-ring">
          {heroIcon}
        </div>
        <h2 className="status-title">{heroTitle}</h2>
        <p className="status-sub">{heroSub}</p>
      </div>

      <div className="status-id-card">
        <div className="status-id-label">Your Application ID</div>
        <div className="status-id-value ui-mono">{appRecord.applicant_id}</div>
        <div className="status-id-hint">Keep this ID. You can use it to chat with our AI assistant.</div>
      </div>

      <div className="status-info-grid">
        <div className="status-info-card"><div className="sic-label">Applicant</div><div className="sic-value">{appRecord.applicant_name}</div></div>
        <div className="status-info-card"><div className="sic-label">Coverage</div><div className="sic-value" style={{ fontSize: 13 }}>{planDisplay}</div></div>
        <div className="status-info-card"><div className="sic-label">Submitted On</div><div className="sic-value">{new Date(appRecord.submitted_at).toLocaleDateString()}</div></div>
        <div className="status-info-card"><div className="sic-label">Medical Document</div><div className="sic-value">{appRecord.pdf_parsed ? 'Parsed ✓' : 'Not uploaded'}</div></div>
        <div className="status-info-card"><div className="sic-label">Total Insured</div><div className="sic-value">{insuredCount} member{insuredCount === 1 ? '' : 's'}</div></div>
      </div>

      {isApproved && appRecord.policy_number && (
        <div className="coverage-summary">
          <div className="coverage-header">
            <h3>Your Policy Coverage</h3>
            <button className="coverage-download-btn" onClick={handleDownloadPdf}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF Report
            </button>
          </div>

          <div className="coverage-policy-info">
            <div className="coverage-field"><span className="coverage-label">Policy Number</span><span className="coverage-value">{appRecord.policy_number}</span></div>
            <div className="coverage-field"><span className="coverage-label">Effective Date</span><span className="coverage-value">{appRecord.policy_start_date || '—'}</span></div>
            <div className="coverage-field"><span className="coverage-label">Expiry Date</span><span className="coverage-value">{appRecord.policy_end_date || '—'}</span></div>
          </div>

          <div className="coverage-premium-box">
            <div className="coverage-premium-main">
              <span className="coverage-premium-label">Annual Premium</span>
              <span className="coverage-premium-amount">${(appRecord.annual_premium || 0).toLocaleString()}</span>
            </div>
            <div className="coverage-premium-secondary">
              <span className="coverage-premium-label">Monthly</span>
              <span className="coverage-premium-monthly">${(appRecord.monthly_premium || 0).toLocaleString()}/mo</span>
            </div>
          </div>

          {appRecord.coverage_details && appRecord.coverage_details.length > 0 && (
            <>
              <div className="coverage-table-section">
                <h4>Coverage Breakdown</h4>
                <table className="coverage-table">
                  <thead>
                    <tr><th>Plan</th><th>Coverage Limit</th><th>Copay</th><th>Deductible</th></tr>
                  </thead>
                  <tbody>
                    {appRecord.coverage_details.map((plan, i) => (
                      <tr key={i}>
                        <td className="coverage-plan-name">{plan.plan_name}</td>
                        <td>${(plan.coverage_limit || 0).toLocaleString()}</td>
                        <td>{plan.copay_percent}%</td>
                        <td>${(plan.deductible || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="coverage-services-section">
                <h4>What's Covered</h4>
                {appRecord.coverage_details.map((plan, i) => (
                  <div key={i} className="coverage-plan-block">
                    <h5>{plan.plan_name}</h5>
                    <ul className="coverage-list coverage-list-covered">
                      {(plan.covered_services || []).map((svc, j) => (
                        <li key={j}>{svc}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="coverage-exclusions-section">
                <h4>What's Not Covered (Exclusions)</h4>
                <ul className="coverage-list coverage-list-excluded">
                  {[...new Set(appRecord.coverage_details.flatMap(p => p.exclusions || []))].map((exc, i) => (
                    <li key={i}>{exc}</li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {appRecord.conditions && (
            <div className="coverage-conditions">
              <h4>Special Conditions</h4>
              <p>{appRecord.conditions}</p>
            </div>
          )}
        </div>
      )}

      {isRejected && (
        <div className="rejection-summary">
          <h3>Decision Details</h3>
          {appRecord.decision_notes && (
            <div className="rejection-reason">
              <h4>Reason</h4>
              <p>{appRecord.decision_notes}</p>
            </div>
          )}
          {appRecord.conditions && (
            <div className="rejection-conditions">
              <h4>Additional Information</h4>
              <p>{appRecord.conditions}</p>
            </div>
          )}
          <div className="rejection-next-steps">
            <h4>Next Steps</h4>
            <ul>
              <li>Contact our support team for more information about this decision</li>
              <li>Submit a new application after addressing the noted concerns</li>
              <li>Request a review of this decision within 30 days</li>
            </ul>
          </div>
        </div>
      )}

      {appRecord.members && appRecord.members.length > 0 && (
        <div className="status-members">
          <div className="status-timeline-head">Insured Members</div>
          <table className="members-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Relationship</th>
                <th>Gender</th>
                <th>Date of Birth</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>{appRecord.applicant_name}</td>
                <td>Principal</td>
                <td>{appRecord.gender === 'M' ? 'Male' : appRecord.gender === 'F' ? 'Female' : '-'}</td>
                <td>{appRecord.dob_day && appRecord.dob_month && appRecord.dob_year ? `${appRecord.dob_day}/${appRecord.dob_month}/${appRecord.dob_year}` : '-'}</td>
              </tr>
              {appRecord.members.map((m, i) => (
                <tr key={i}>
                  <td>{i + 2}</td>
                  <td>{[m.first_name, m.last_name].filter(Boolean).join(' ') || '-'}</td>
                  <td>{m.relationship || '-'}</td>
                  <td>{m.gender === 'M' ? 'Male' : m.gender === 'F' ? 'Female' : '-'}</td>
                  <td>{m.dob_day && m.dob_month && m.dob_year ? `${m.dob_day}/${m.dob_month}/${m.dob_year}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="status-timeline">
        <div className="status-timeline-head">Application Progress</div>
        {steps.map((s, i) => (
          <div key={i} className="timeline-item">
            <div className={`timeline-icon ti-${s.status}`}>
              {s.status === 'done'    && <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
              {s.status === 'active'  && <div className="pulse-dot" />}
              {s.status === 'pending' && <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>}
            </div>
            <div className="timeline-content">
              <div className={`timeline-step-name${s.status === 'pending' ? ' pending-step' : ''}`}>{s.name}</div>
              <div className="timeline-step-desc">{s.desc}</div>
              <div style={{ fontSize: 11, color: 'var(--p-muted)', marginTop: 3 }}>{s.time}</div>
            </div>
            <span className={`timeline-badge badge-${s.status}`}>
              {s.status === 'done' ? 'Complete' : s.status === 'active' ? 'In Progress' : 'Pending'}
            </span>
          </div>
        ))}
      </div>

      <div className="status-actions">
        <button className="status-cta status-cta-primary" onClick={() => onSwitchToChat && onSwitchToChat(appRecord.applicant_id)}>
          Chat with AI about my application
        </button>
        <button className="status-cta status-cta-ghost" onClick={onNewApplication}>Submit another application</button>
        <button className="status-cta status-cta-ghost" onClick={onBackToDashboard}>Back to application home</button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   CLAIMS SUBMIT FORM
   ════════════════════════════════════════════════════════════════ */
const CLAIM_TYPE_CONFIG = {
  'Medical/Hospitalization': {
    category: 'Medical',
    label: 'Medical / Hospitalization',
    descriptionHint: 'Describe the medical condition, treatment received, and hospital/clinic stay details.',
    requiredInfo: ['Hospital or clinic name', 'Admission and discharge dates', 'Diagnosis or condition treated', 'Itemized treatment costs'],
    supportingDocs: ['Hospital discharge summary or admission report', 'Itemized medical invoice/bill', 'Doctor\'s referral letter (if applicable)', 'Prescription records related to the treatment'],
    providerLabel: 'Hospital / Clinic Name',
  },
  'Outpatient/Ambulatory': {
    category: 'Medical',
    label: 'Outpatient / Ambulatory Care',
    descriptionHint: 'Describe the outpatient procedure or visit, including the reason and treatment performed.',
    requiredInfo: ['Clinic or medical center name', 'Date of visit', 'Procedure or treatment performed', 'Total cost of services'],
    supportingDocs: ['Outpatient visit receipt or invoice', 'Doctor\'s consultation notes', 'Lab test results (if applicable)', 'Referral letter from primary care physician'],
    providerLabel: 'Clinic / Medical Center',
  },
  'Prescription Drugs': {
    category: 'Medical',
    label: 'Prescription Drugs',
    descriptionHint: 'List the prescribed medications, dosage, and the condition they treat.',
    requiredInfo: ['Pharmacy name', 'Prescribing doctor\'s name', 'Medication names and dosages', 'Total cost of medications'],
    supportingDocs: ['Pharmacy receipt with medication details', 'Doctor\'s prescription (original or copy)', 'Pharmacy printout showing medication and price breakdown'],
    providerLabel: 'Pharmacy Name',
  },
  "Doctor's Visit": {
    category: 'Medical',
    label: "Doctor's Visit",
    descriptionHint: 'Describe the reason for the visit, symptoms, and any diagnosis or follow-up recommended.',
    requiredInfo: ['Doctor\'s name and specialty', 'Date of consultation', 'Reason for visit / symptoms', 'Consultation fee amount'],
    supportingDocs: ['Doctor\'s consultation receipt or invoice', 'Consultation notes or medical report', 'Referral letter (if specialist visit)'],
    providerLabel: "Doctor's Name / Practice",
  },
  'Accident/Collision': {
    category: 'Auto',
    label: 'Accident / Collision',
    descriptionHint: 'Describe the accident: where and when it occurred, vehicles involved, and the extent of damage.',
    requiredInfo: ['Date, time, and location of accident', 'Other vehicle/driver details (if applicable)', 'Description of damage to your vehicle', 'Repair estimate or actual repair cost'],
    supportingDocs: ['Police accident report', 'Photos of vehicle damage (before/after)', 'Repair shop estimate or invoice', 'Witness statements (if available)'],
    providerLabel: 'Repair Shop / Body Shop',
  },
  'Third-Party Liability': {
    category: 'Auto',
    label: 'Third-Party Liability',
    descriptionHint: 'Describe the incident where a third party suffered damage or injury due to your vehicle.',
    requiredInfo: ['Date and location of incident', 'Third party\'s name and contact information', 'Description of damage or injury caused', 'Claimed amount by the third party'],
    supportingDocs: ['Police report', 'Third-party damage assessment or medical report', 'Correspondence or legal notice from third party', 'Photos of the incident scene or damage'],
    providerLabel: 'Legal Representative / Third Party Contact',
  },
  'Theft': {
    category: 'Auto',
    label: 'Vehicle Theft',
    descriptionHint: 'Describe when and where the vehicle was stolen, and any circumstances surrounding the theft.',
    requiredInfo: ['Date and time theft was discovered', 'Location where vehicle was last seen', 'Police report reference number', 'Vehicle make, model, year, and license plate'],
    supportingDocs: ['Police theft report (mandatory)', 'Vehicle registration documents', 'Photos of the vehicle (most recent)', 'Any security camera footage reference'],
    providerLabel: 'Police Station / Reference',
  },
  'Property Damage': {
    category: 'Auto',
    label: 'Vehicle Property Damage',
    descriptionHint: 'Describe the property damage to your vehicle (e.g., vandalism, falling objects, road hazards).',
    requiredInfo: ['Date and location of damage', 'Cause of damage', 'Description of affected parts', 'Repair estimate amount'],
    supportingDocs: ['Photos of the damage', 'Repair shop estimate or invoice', 'Police report (if vandalism)', 'Incident report from parking facility (if applicable)'],
    providerLabel: 'Repair Shop Name',
  },
  'Fire Damage': {
    category: 'Property',
    label: 'Fire Damage',
    descriptionHint: 'Describe the fire incident: how it started (if known), extent of damage to property and contents.',
    requiredInfo: ['Date and time of fire', 'Property address', 'Cause of fire (if known)', 'List of damaged/destroyed items with estimated values'],
    supportingDocs: ['Fire department report', 'Photos of fire damage (interior and exterior)', 'Property damage assessment by certified assessor', 'List of damaged items with purchase receipts or valuations', 'Temporary housing receipts (if displaced)'],
    providerLabel: 'Fire Department / Assessor',
  },
  'Flood': {
    category: 'Property',
    label: 'Flood Damage',
    descriptionHint: 'Describe the flooding: water source, affected areas of the property, and damaged contents.',
    requiredInfo: ['Date flooding occurred', 'Property address', 'Source of flooding (river, pipe burst, storm)', 'Affected areas and list of damaged items'],
    supportingDocs: ['Photos of flood damage and water levels', 'Professional damage assessment report', 'Receipts for emergency cleanup/repairs', 'Municipal flood advisory or weather report (if applicable)', 'Inventory of damaged belongings with values'],
    providerLabel: 'Restoration Company / Assessor',
  },
  'Theft/Burglary': {
    category: 'Property',
    label: 'Theft / Burglary',
    descriptionHint: 'Describe the break-in: how entry was gained, what was stolen, and any damage to the property.',
    requiredInfo: ['Date and time theft was discovered', 'Property address', 'Point of entry / how break-in occurred', 'List of stolen items with estimated values'],
    supportingDocs: ['Police report (mandatory)', 'Photos of forced entry or damage', 'List of stolen items with purchase receipts or valuations', 'Security camera footage reference (if available)', 'Locksmith or repair receipts for damaged entry points'],
    providerLabel: 'Police Station / Reference Number',
  },
  'Natural Disaster': {
    category: 'Property',
    label: 'Natural Disaster',
    descriptionHint: 'Describe the natural event (earthquake, hurricane, tornado, etc.) and resulting property damage.',
    requiredInfo: ['Date of the event', 'Type of natural disaster', 'Property address', 'Description of structural and content damage'],
    supportingDocs: ['Government disaster declaration or weather service report', 'Photos of property damage', 'Professional structural assessment report', 'Contractor repair estimates', 'Inventory of damaged contents with values'],
    providerLabel: 'Assessor / Contractor',
  },
  'Death Benefit': {
    category: 'Life',
    label: 'Death Benefit',
    descriptionHint: 'Provide details about the policyholder\'s passing, including date, cause, and beneficiary information.',
    requiredInfo: ['Date of death', 'Cause of death', 'Beneficiary name and relationship to policyholder', 'Policy number'],
    supportingDocs: ['Certified death certificate (mandatory)', 'Attending physician\'s statement or medical report', 'Proof of beneficiary identity (government-issued ID)', 'Policy document or certificate'],
    providerLabel: 'Attending Physician / Hospital',
  },
  'Disability': {
    category: 'Life',
    label: 'Disability Claim',
    descriptionHint: 'Describe the disability: medical condition, how it affects daily life/work capacity, and prognosis.',
    requiredInfo: ['Date disability began', 'Medical condition causing disability', 'Impact on ability to work', 'Treating physician\'s name and specialty'],
    supportingDocs: ['Physician\'s disability statement or medical report', 'Diagnostic test results (MRI, X-ray, lab work)', 'Employer letter confirming inability to work (if applicable)', 'Rehabilitation or treatment plan documentation'],
    providerLabel: 'Treating Physician / Specialist',
  },
};

function ClaimsSubmitForm({ user, applicantId, onSubmitted, onCancel }) {
  const [formData, setFormData] = useState({
    claim_type: '',
    incident_date: '',
    claim_amount: '',
    description: '',
    provider_name: '',
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Auto/car FNOL: structured vehicle + accident data and accident photos
  const [vehicle, setVehicle] = useState({ vin: '', year: '', make: '', model: '', mileage: '', plate: '' });
  const [accident, setAccident] = useState({ time: '', location: '', police_report_no: '', fault_party: '', drivable: '' });
  const [coverageType, setCoverageType] = useState('');
  const [photos, setPhotos] = useState([]); // File[]

  const selectedConfig = CLAIM_TYPE_CONFIG[formData.claim_type] || null;
  const isAuto = selectedConfig?.category === 'Auto';

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleVehicleChange = (e) => {
    setVehicle(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const handleAccidentChange = (e) => {
    setAccident(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const handlePhotosChange = (e) => {
    setPhotos(Array.from(e.target.files || []).slice(0, 8));
  };

  const fileToBase64 = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(f);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      let claim_document = null;
      if (file) {
        claim_document = await fileToBase64(file);
      }

      const payload = {
        applicant_id: applicantId,
        email: user.email,
        claim_type: formData.claim_type,
        incident_date: formData.incident_date,
        claim_amount: parseFloat(formData.claim_amount),
        description: formData.description,
        provider_name: formData.provider_name,
        claim_document,
      };

      // Auto/car FNOL: attach structured vehicle/accident data + accident photos
      if (isAuto) {
        const photoB64 = await Promise.all(photos.map(fileToBase64));
        payload.line_of_business = 'auto';
        payload.coverage_type = coverageType;
        payload.vehicle = {
          ...vehicle,
          year: vehicle.year ? parseInt(vehicle.year, 10) : '',
          mileage: vehicle.mileage ? parseInt(vehicle.mileage, 10) : '',
        };
        payload.accident = {
          date: formData.incident_date,
          ...accident,
        };
        payload.photos = photoB64;
      }

      const res = await fetch(`${API_URL}/submit-claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to submit claim');
      }
      setSuccess(true);
      setTimeout(() => onSubmitted(), 2000);
    } catch (err) {
      setError(err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="form-shell">
        <div className="field-block" style={{ textAlign: 'center', padding: '3rem' }}>
          <h2>Claim Submitted Successfully</h2>
          <p>Your claim has been filed and is under review. You will receive an email confirmation shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-shell">
      <h2 style={{ marginBottom: '0.5rem' }}>File a Claim</h2>
      <p style={{ color: '#7f8c8d', marginBottom: '1.5rem', fontSize: '0.9rem' }}>Select your claim type below to see exactly what information and documents you need to provide.</p>
      {error && <div className="field-block" style={{ color: '#c0392b', background: '#fdecea', padding: '0.75rem 1rem', borderRadius: '6px' }}>{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="field-block">
          <label>Claim Type <span style={{ color: '#c0392b' }}>*</span></label>
          <select name="claim_type" value={formData.claim_type} onChange={handleChange} required className="seg-btn">
            <option value="">— Select your claim type —</option>
            <optgroup label="Medical Insurance">
              <option value="Medical/Hospitalization">Medical / Hospitalization</option>
              <option value="Outpatient/Ambulatory">Outpatient / Ambulatory Care</option>
              <option value="Prescription Drugs">Prescription Drugs</option>
              <option value="Doctor's Visit">Doctor's Visit</option>
            </optgroup>
            <optgroup label="Auto Insurance">
              <option value="Accident/Collision">Accident / Collision</option>
              <option value="Third-Party Liability">Third-Party Liability</option>
              <option value="Theft">Vehicle Theft</option>
              <option value="Property Damage">Vehicle Property Damage</option>
            </optgroup>
            <optgroup label="Property Insurance">
              <option value="Fire Damage">Fire Damage</option>
              <option value="Flood">Flood Damage</option>
              <option value="Theft/Burglary">Theft / Burglary</option>
              <option value="Natural Disaster">Natural Disaster</option>
            </optgroup>
            <optgroup label="Life Insurance">
              <option value="Death Benefit">Death Benefit</option>
              <option value="Disability">Disability</option>
            </optgroup>
          </select>
        </div>

        {selectedConfig && (
          <div className="field-block" style={{ background: '#f0f7ff', border: '1px solid #b3d4fc', borderRadius: '8px', padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
            <div style={{ fontWeight: 600, color: '#2c3e50', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
              📋 What you'll need for a {selectedConfig.label} claim:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#34495e', marginBottom: '0.4rem' }}>Required Information</div>
                <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem', color: '#555', lineHeight: '1.6' }}>
                  {selectedConfig.requiredInfo.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#34495e', marginBottom: '0.4rem' }}>Supporting Documents (PDF)</div>
                <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem', color: '#555', lineHeight: '1.6' }}>
                  {selectedConfig.supportingDocs.map((doc, i) => <li key={i}>{doc}</li>)}
                </ul>
              </div>
            </div>
          </div>
        )}

        {isAuto && (
          <>
            <div className="field-block" style={{ background: '#fafbfc', border: '1px solid #e1e8ed', borderRadius: '8px', padding: '1rem 1.25rem' }}>
              <div style={{ fontWeight: 600, color: '#2c3e50', marginBottom: '0.75rem', fontSize: '0.95rem' }}>🚗 Vehicle Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label>VIN</label>
                  <input type="text" name="vin" value={vehicle.vin} onChange={handleVehicleChange} placeholder="Vehicle Identification Number" />
                </div>
                <div>
                  <label>License Plate</label>
                  <input type="text" name="plate" value={vehicle.plate} onChange={handleVehicleChange} placeholder="Plate number" />
                </div>
                <div>
                  <label>Year</label>
                  <input type="number" name="year" value={vehicle.year} onChange={handleVehicleChange} placeholder="e.g., 2019" min="1950" max="2030" />
                </div>
                <div>
                  <label>Make</label>
                  <input type="text" name="make" value={vehicle.make} onChange={handleVehicleChange} placeholder="e.g., Toyota" />
                </div>
                <div>
                  <label>Model</label>
                  <input type="text" name="model" value={vehicle.model} onChange={handleVehicleChange} placeholder="e.g., Camry" />
                </div>
                <div>
                  <label>Mileage</label>
                  <input type="number" name="mileage" value={vehicle.mileage} onChange={handleVehicleChange} placeholder="Odometer reading" min="0" />
                </div>
              </div>
              <div style={{ marginTop: '0.75rem' }}>
                <label>Coverage Type <span style={{ color: '#c0392b' }}>*</span></label>
                <select className="seg-btn" value={coverageType} onChange={(e) => setCoverageType(e.target.value)} required={isAuto}>
                  <option value="">— Select coverage type —</option>
                  <option value="collision">Collision</option>
                  <option value="comprehensive">Comprehensive</option>
                  <option value="liability">Liability</option>
                </select>
              </div>
            </div>

            <div className="field-block" style={{ background: '#fafbfc', border: '1px solid #e1e8ed', borderRadius: '8px', padding: '1rem 1.25rem' }}>
              <div style={{ fontWeight: 600, color: '#2c3e50', marginBottom: '0.75rem', fontSize: '0.95rem' }}>💥 Accident Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label>Time of Accident</label>
                  <input type="time" name="time" value={accident.time} onChange={handleAccidentChange} />
                </div>
                <div>
                  <label>Police Report #</label>
                  <input type="text" name="police_report_no" value={accident.police_report_no} onChange={handleAccidentChange} placeholder="If filed" />
                </div>
                <div style={{ gridColumn: '1 / span 2' }}>
                  <label>Location</label>
                  <input type="text" name="location" value={accident.location} onChange={handleAccidentChange} placeholder="Where the accident occurred" />
                </div>
                <div>
                  <label>Who was at fault?</label>
                  <select className="seg-btn" name="fault_party" value={accident.fault_party} onChange={handleAccidentChange}>
                    <option value="">— Select —</option>
                    <option value="insured">Me (insured)</option>
                    <option value="third_party">Other party</option>
                    <option value="shared">Shared / unclear</option>
                    <option value="none">No other party</option>
                  </select>
                </div>
                <div>
                  <label>Is the vehicle drivable?</label>
                  <select className="seg-btn" name="drivable" value={accident.drivable} onChange={handleAccidentChange}>
                    <option value="">— Select —</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="field-block">
              <label>Accident Photos (up to 8)</label>
              <input type="file" accept="image/*" multiple onChange={handlePhotosChange} />
              <p style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: '0.4rem' }}>
                Upload clear photos of the damage from multiple angles. Our AI appraiser analyzes these to estimate severity and repair cost.
              </p>
              {photos.length > 0 && (
                <div style={{ fontSize: '0.8rem', color: '#27ae60', marginTop: '0.3rem' }}>{photos.length} photo(s) selected</div>
              )}
            </div>
          </>
        )}

        <div className="field-block">
          <label>{selectedConfig ? selectedConfig.providerLabel : 'Provider / Service Name'} <span style={{ color: '#c0392b' }}>*</span></label>
          <input type="text" name="provider_name" value={formData.provider_name} onChange={handleChange} required
            placeholder={selectedConfig ? `Enter ${selectedConfig.providerLabel.toLowerCase()}` : 'Enter provider name'} />
        </div>
        <div className="field-block">
          <label>Incident Date <span style={{ color: '#c0392b' }}>*</span></label>
          <input type="date" name="incident_date" value={formData.incident_date} onChange={handleChange} required />
        </div>
        <div className="field-block">
          <label>Claim Amount ($) <span style={{ color: '#c0392b' }}>*</span></label>
          <input type="number" name="claim_amount" value={formData.claim_amount} onChange={handleChange} required min="0" step="0.01"
            placeholder="Enter the total amount you are claiming" />
        </div>
        <div className="field-block">
          <label>Description <span style={{ color: '#c0392b' }}>*</span></label>
          <textarea name="description" value={formData.description} onChange={handleChange} required rows={4}
            placeholder={selectedConfig ? selectedConfig.descriptionHint : 'Describe your claim in detail...'} />
        </div>
        <div className="field-block">
          <label>Supporting Document (PDF)</label>
          <input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files[0])} />
          <p style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: '0.4rem' }}>
            {selectedConfig
              ? `Recommended: ${selectedConfig.supportingDocs[0]}. Combine multiple documents into a single PDF if possible.`
              : 'Upload a PDF document supporting your claim (invoice, report, receipt, etc.).'
            }
          </p>
        </div>
        <div className="field-block" style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
          <button type="submit" className="seg-btn" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Claim'}
          </button>
          <button type="button" className="seg-btn" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   MY CLAIMS VIEW
   ════════════════════════════════════════════════════════════════ */
function MyClaimsView({ user, onBack }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/claims?email=${encodeURIComponent(user.email)}`)
      .then(r => r.json())
      .then(data => setClaims(data.claims || []))
      .catch(() => setClaims([]))
      .finally(() => setLoading(false));
  }, [user.email]);

  const fnolKeys = ['intake', 'ocr', 'construction', 'completeness', 'preauth_link', 'fraud_signals', 'routing'];

  return (
    <div className="dashboard-shell">
      <div className="dashboard-list">
        <div className="dashboard-list-head">
          <div>
            <h2 className="dashboard-list-title">My Claims</h2>
            <p className="dashboard-list-subtitle">Track the status of your filed claims.</p>
          </div>
          <button className="dashboard-inline-btn" onClick={onBack}>Back</button>
        </div>

        {loading ? (
          <div className="dashboard-empty"><p>Loading claims...</p></div>
        ) : claims.length === 0 ? (
          <div className="dashboard-empty">
            <div className="dashboard-empty-title">No claims filed yet</div>
            <p className="dashboard-empty-copy">File a claim from an approved application to see it here.</p>
          </div>
        ) : (
          <div className="dashboard-list-body">
            {claims.map((claim) => {
              const fnolPipeline = claim.fnol_pipeline_status || {};
              const hasFnol = Object.keys(fnolPipeline).length > 0;
              return (
                <article key={claim.claim_id} className="dashboard-app-card">
                  <div className="dashboard-app-main">
                    <div className="dashboard-app-id ui-mono">{claim.claim_id}</div>
                    <div className="dashboard-app-name">{claim.claim_type} claim</div>
                    <div className="dashboard-app-meta">
                      <span>${Number(claim.amount || claim.claim_amount || 0).toLocaleString()}</span>
                      <span>{claim.submitted_at ? new Date(claim.submitted_at).toLocaleDateString() : ''}</span>
                    </div>
                    {hasFnol && (
                      <div className="provider-mini-pipeline" style={{ marginTop: '6px' }}>
                        {fnolKeys.map(key => {
                          const st = fnolPipeline[key] || 'pending';
                          return <span key={key} className={`mini-step ${st === 'complete' || st === 'skipped' ? 'step-done' : st === 'in_progress' ? 'step-active' : 'step-pending'}`} />;
                        })}
                        <span style={{ fontSize: '0.7rem', color: '#64748b', marginLeft: '6px' }}>FNOL</span>
                      </div>
                    )}
                  </div>
                  <div className="dashboard-app-side">
                    <span className={`dashboard-status dashboard-status-${claim.status || 'under_review'}`}>
                      {claim.status ? claim.status.replace(/_/g, ' ') : 'Under Review'}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   MY PRE-AUTHORIZATIONS VIEW
   ════════════════════════════════════════════════════════════════ */
function MyPreAuthsView({ user, onBack }) {
  const [preauths, setPreauths] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_URL_LOCAL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    const fetchPreauths = async () => {
      setLoading(true);
      try {
        const email = user?.email || user?.username;
        const resp = await fetch(`${API_URL_LOCAL}/preauth/member?email=${encodeURIComponent(email)}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        setPreauths(data.preauths || []);
      } catch (err) {
        console.error('Failed to fetch preauths:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPreauths();
  }, [user, API_URL_LOCAL]);

  const statusColor = (s) => {
    const map = { approved: '#059669', denied: '#dc2626', processing: '#3b82f6', routed_to_human: '#d97706' };
    return map[s] || '#64748b';
  };

  return (
    <div style={{ padding: '24px' }}>
      <button className="portal-home-btn" onClick={onBack} style={{ marginBottom: '16px' }}>← Back</button>
      <h2 style={{ marginBottom: '16px' }}>My Pre-Authorizations</h2>
      {loading ? (
        <div className="provider-loading">Loading…</div>
      ) : preauths.length === 0 ? (
        <div className="provider-empty">No pre-authorization requests found for your account.</div>
      ) : (
        <div className="provider-requests-table">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Procedure</th>
                <th>Provider</th>
                <th>Status</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {preauths.map(pa => (
                <tr key={pa.preauth_id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{pa.preauth_id}</td>
                  <td>{pa.procedure_code} — {pa.procedure_description}</td>
                  <td>{pa.provider_id}</td>
                  <td><span className="provider-status-badge" style={{ background: statusColor(pa.status) }}>{pa.status}</span></td>
                  <td>{new Date(pa.submitted_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   ROOT — APPLICANT PORTAL
   ════════════════════════════════════════════════════════════════ */
export default function ApplicantPortal({ onSwitchToChat, onAuthChange }) {
  const [user, setUser] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [portalView, setPortalView] = useState('dashboard');
  const [insuranceType, setInsuranceType] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);

  const loadApplications = useCallback(async (email) => {
    const cachedApplications = mergeApplications([], ApplicationsCache.get(email));
    if (cachedApplications.length) {
      setApplications(cachedApplications);
    }

    try {
      const remoteApplications = await fetchApplicationsForEmail(email);
      const nextApplications = mergeApplications(cachedApplications, remoteApplications);

      setApplications(nextApplications);

      if (nextApplications.length) {
        ApplicationsCache.set(email, nextApplications);
      } else {
        ApplicationsCache.clear(email);
      }

      return nextApplications;
    } catch (err) {
      console.error('Failed to fetch application status:', err);
      return cachedApplications;
    }
  }, []);

  const restoreUserSession = useCallback(async (restoredUser) => {
    // Call onAuthChange BEFORE setUser so App-level navigation happens
    // before this component renders its dashboard view
    onAuthChange?.(restoredUser);
    setUser(restoredUser);
    const nextApplications = await loadApplications(restoredUser.email);
    setSelectedApplicationId(nextApplications[0]?.applicant_id || null);
    setPortalView('dashboard');
  }, [loadApplications, onAuthChange]);

  useEffect(() => {
    let cancelled = false;
    const pool = getUserPool();

    if (!pool) {
      setSessionReady(true);
      return () => {};
    }

    const currentUser = pool.getCurrentUser();
    if (!currentUser) {
      setSessionReady(true);
      return () => {};
    }

    currentUser.getSession(async (e, session) => {
      if (!cancelled && !e && session && session.isValid()) {
        // Force-expire sessions older than SESSION_MAX_MS
        const loginAt = parseInt(localStorage.getItem(LOGIN_TS_KEY) || '0', 10);
        if (loginAt && Date.now() - loginAt > SESSION_MAX_MS) {
          currentUser.signOut();
          localStorage.removeItem(LOGIN_TS_KEY);
        } else {
          const pl = session.getIdToken().payload;
          const groups = pl['cognito:groups'] || [];
          const restoredUser = {
            name: pl.name || pl.email,
            email: pl.email,
            phone: pl.phone_number || '',
            groups,
          };

          await restoreUserSession(restoredUser);
        }
      }

      if (!cancelled) {
        setSessionReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [restoreUserSession]);

  useEffect(() => {
    if (!applications.length) {
      setSelectedApplicationId(null);
      return;
    }

    if (!selectedApplicationId || !applications.some(application => application.applicant_id === selectedApplicationId)) {
      setSelectedApplicationId(applications[0].applicant_id);
    }
  }, [applications, selectedApplicationId]);

  const handleLogin = async (nextUser) => {
    await restoreUserSession(nextUser);
  };
  const handleSubmitted = (application) => {
    setApplications((currentApplications) => {
      const nextApplications = mergeApplications(currentApplications, [application]);
      if (user) {
        ApplicationsCache.set(user.email, nextApplications);
      }
      return nextApplications;
    });

    setSelectedApplicationId(application.applicant_id);
    setPortalView('status');
  };

  const handleStartApplication = () => {
    setInsuranceType(null);
    setPortalView('type-picker');
  };

  const handleTypeSelected = (type) => {
    setInsuranceType(type);
    setPortalView('form');
  };

  const handleViewApplication = (applicantId) => {
    setSelectedApplicationId(applicantId);
    setPortalView('status');
  };

  const handleBackToDashboard = () => {
    setPortalView('dashboard');
    setInsuranceType(null);
  };

  const handleFileClaim = (applicantId) => {
    setSelectedApplicationId(applicantId);
    setPortalView('claims-form');
  };

  const handleViewClaims = () => {
    setPortalView('my-claims');
  };

  const handleViewPreauths = () => {
    setPortalView('my-preauths');
  };

  const handleSignOut = () => {
    const pool = getUserPool();
    if (pool) {
      const currentUser = pool.getCurrentUser();
      if (currentUser) currentUser.signOut();
    }

    localStorage.removeItem(LOGIN_TS_KEY);

    if (user) {
      ApplicationsCache.clear(user.email);
    }

    setUser(null);
    setApplications([]);
    setSelectedApplicationId(null);
    setPortalView('dashboard');
    onAuthChange?.(null);
  };

  if (!sessionReady) {
    return <div className="portal"><div className="portal-loading">Loading...</div></div>;
  }

  const selectedApplication = applications.find(application => application.applicant_id === selectedApplicationId) || null;
  const view = !user ? 'auth' : (portalView === 'status' && !selectedApplication ? 'dashboard' : portalView);
  const portalLabel = view === 'type-picker'
    ? 'New application'
    : view === 'form'
      ? 'Application in progress'
      : view === 'status'
        ? 'Status tracking'
        : view === 'claims-form'
          ? 'File a claim'
          : view === 'my-claims'
            ? 'My claims'
            : view === 'my-preauths'
              ? 'My pre-authorizations'
              : 'Applicant home';

  return (
    <div className="portal">
      {user && (
        <div className="portal-topbar">
          <div className="portal-topbar-left">
            <span className="portal-welcome">Welcome, <strong>{user.name.split(' ')[0]}</strong></span>
            <span className="portal-context-chip">{portalLabel}</span>
            <span className="portal-application-count">{applications.length} application{applications.length === 1 ? '' : 's'}</span>
          </div>
          <div className="portal-topbar-right">
            {view !== 'dashboard' && (
              <button className="portal-home-btn" onClick={handleBackToDashboard}>
                Application home
              </button>
            )}
            <button className="portal-home-btn" onClick={handleViewClaims}>My Claims</button>
            <button className="portal-home-btn" onClick={handleViewPreauths}>My Pre-Auths</button>
            <button className="portal-signout" onClick={handleSignOut}>Sign out</button>
          </div>
        </div>
      )}
      {view === 'auth' && <AuthScreen onLogin={handleLogin} />}
      {view === 'dashboard' && user && (
        <ApplicantDashboard
          user={user}
          applications={applications}
          onStartApplication={handleStartApplication}
          onViewApplication={handleViewApplication}
          onSwitchToChat={onSwitchToChat}
          onFileClaim={handleFileClaim}
        />
      )}
      {view === 'type-picker' && user && (
        <InsuranceTypePicker onSelect={handleTypeSelected} onCancel={handleBackToDashboard} />
      )}
      {view === 'form' && user && insuranceType === 'medical'  && <ApplicationForm user={user} onSubmitted={handleSubmitted} />}
      {view === 'form' && user && insuranceType === 'car'      && <CarInsuranceForm user={user} onSubmitted={handleSubmitted} />}
      {view === 'form' && user && insuranceType === 'property' && <PropertyInsuranceForm user={user} onSubmitted={handleSubmitted} />}
      {view === 'form' && user && insuranceType === 'life'     && <LifeInsuranceForm user={user} onSubmitted={handleSubmitted} />}
      {view === 'status' && selectedApplication && (
        <StatusScreen
          appRecord={selectedApplication}
          onSwitchToChat={onSwitchToChat}
          onNewApplication={handleStartApplication}
          onBackToDashboard={handleBackToDashboard}
        />
      )}
      {view === 'claims-form' && user && selectedApplicationId && (
        <ClaimsSubmitForm user={user} applicantId={selectedApplicationId} onSubmitted={handleBackToDashboard} onCancel={handleBackToDashboard} />
      )}
      {view === 'my-claims' && user && (
        <MyClaimsView user={user} onBack={handleBackToDashboard} />
      )}
      {view === 'my-preauths' && user && (
        <MyPreAuthsView user={user} onBack={handleBackToDashboard} />
      )}
    </div>
  );
}
